/**
 * PostEditPage Component for LOOPINO AI Mobile
 * Complete post editing page with image management, text editing, and AI features
 */

import React, { useState, useEffect, useCallback, useRef, useMemo } from 'react';
import './PostEditPage.css';
import AIRegenerationModal from './AIRegenerationModal';
import useMobilePostReview from '../../../hooks/useMobilePostReview.js';
import Skeleton from '../../effects/Skeleton';
import ImageGalleryPage from '../MobileImageGallery/ImageGalleryPage';
import postService from '../../../services/PostService';
import useApi from '../../../hooks/useApi';
import { API_CONFIG } from '../../../config/api';
import iconAi from '../../../assets/images/icons/icon-ai.svg';
import useLanguage from '../../../hooks/useLanguage';
import {
  SUPPORTED_MEDIA_ACCEPT,
  SUPPORTED_MEDIA_LABEL,
  SUPPORTED_MEDIA_MIME_TYPES,
  isSupportedMediaFile
} from '../mediaUploadConfig';
import { useMobileAlert } from '../common/alerts/MobileAlertContext';
import { useMobileSwipeBackContext } from '../MobileSwipeBackContext';

const generateMediaId = () => {
  if (typeof crypto !== 'undefined' && crypto.randomUUID) {
    return crypto.randomUUID();
  }
  return `media-${Date.now()}-${Math.random().toString(16).slice(2)}`;
};

const toAbsoluteMediaUrl = (url) => {
  if (!url) {
    return null;
  }

  try {
    const resolved = new URL(url, API_CONFIG.BASE_URL);
    return resolved.href;
  } catch (error) {
    console.warn('[PostEditPage] Failed to resolve media URL, using original value', { url, error });
    return url;
  }
};

const buildMediaArrayFromUrls = (urls = []) =>
  urls
    .filter(Boolean)
    .map((url) => {
      const absoluteUrl = toAbsoluteMediaUrl(url);
      const finalUrl = absoluteUrl || url;
      return {
        id: generateMediaId(),
        type: 'url',
        url: finalUrl,
        previewUrl: absoluteUrl || url
      };
    });

const buildImagesFromMediaArray = (mediaArray = []) =>
  mediaArray.map((item) => item.previewUrl || item.url || '');

const buildMediaUrlsFromMediaArray = (mediaArray = []) =>
  mediaArray.map((item) => item.url || item.previewUrl || '').filter(Boolean);

const extractStatusValue = (status) => {
  if (status === undefined || status === null) {
    return null;
  }

  if (typeof status === 'string' || typeof status === 'number') {
    return String(status);
  }

  if (typeof status === 'object') {
    const candidate = status.value ?? status.current ?? status.status ?? status.state;
    if (candidate !== undefined && candidate !== null) {
      return String(candidate);
    }
  }

  return null;
};

const getImageClassNames = (baseClass, loadingProps) =>
  [
    baseClass,
    loadingProps ? 'mobile-image-with-loading' : '',
    loadingProps?.className || ''
  ].filter(Boolean).join(' ');


// Save Icon component
const SaveIcon = ({ size = 18 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M19 21H5C4.44772 21 4 20.5523 4 20V4C4 3.44772 4.44772 3 5 3H16L20 7V20C20 20.5523 19.5523 21 19 21Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    <path d="M17 21V13H7V21" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    <path d="M7 3V7H15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

// Icon Components
const CloseIcon = ({ size = 24 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M18 6L6 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    <path d="M6 6L18 18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const BackIcon = ({ size = 24 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M19 12H5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    <path d="M12 19L5 12L12 5" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const UploadIcon = ({ size = 18 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    <polyline points="17 8 12 3 7 8" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    <line x1="12" y1="3" x2="12" y2="15" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const GalleryIcon = ({ size = 18 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <rect x="3" y="4" width="18" height="16" rx="2" stroke="currentColor" strokeWidth="2"/>
    <path d="M3 15l4.5-3 3.5 2.5L18 11l3 4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    <circle cx="8" cy="8.5" r="1.5" fill="currentColor"/>
  </svg>
);

const AIIcon = ({ size = 18 }) => (
  <svg width={size} height={size} viewBox="0 0 18 22" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M2.56791 18.8123V17.9563H1.71194C1.23925 17.9563 0.856061 17.573 0.855972 17.1003C0.855972 16.6276 1.2392 16.2444 1.71194 16.2444H2.56791V15.3884C2.56791 14.9157 2.95119 14.5325 3.42389 14.5324C3.89666 14.5324 4.27986 14.9156 4.27986 15.3884V16.2444H5.13583C5.6086 16.2444 5.9918 16.6276 5.9918 17.1003C5.99171 17.573 5.60855 17.9563 5.13583 17.9563H4.27986V18.8123C4.27983 19.285 3.89664 19.6683 3.42389 19.6683C2.95121 19.6682 2.56794 19.285 2.56791 18.8123ZM10.4767 4.80819C10.6996 4.77883 10.927 4.79315 11.1455 4.85199H11.1446C11.5422 4.95852 11.8004 5.23185 11.9627 5.47207C12.1242 5.71114 12.2481 6.00066 12.3529 6.29619C12.5623 6.88697 12.7703 7.72584 13.0287 8.7586L13.1272 9.12687C13.1277 9.1282 13.1278 9.12959 13.1282 9.13085C13.1298 9.13146 13.1315 9.13219 13.1332 9.13284C13.2028 9.15873 13.3021 9.18814 13.4955 9.24332L14.7028 9.59467C15.0616 9.70418 15.3747 9.80788 15.6344 9.91118C15.8954 10.015 16.1552 10.1383 16.3729 10.2984C16.5953 10.4619 16.832 10.7075 16.9343 11.0667L16.9632 11.1822C17.0206 11.4541 17.0108 11.7369 16.9343 12.0053C16.832 12.3646 16.5953 12.6102 16.3729 12.7737C16.1552 12.9337 15.8954 13.0571 15.6344 13.1609C15.3747 13.2642 15.0616 13.3679 14.7028 13.4774L13.4955 13.8287C13.3017 13.884 13.2026 13.9134 13.1332 13.9392C13.1316 13.9398 13.1297 13.9396 13.1282 13.9402C13.1277 13.9417 13.1277 13.9436 13.1272 13.9452C13.1037 14.0159 13.0776 14.1158 13.0287 14.3125V14.3135C12.7703 15.3462 12.5623 16.1851 12.3529 16.7759C12.2482 17.0712 12.124 17.36 11.9627 17.599C11.8004 17.8393 11.5424 18.1135 11.1446 18.2201L11.1436 18.2191C10.8524 18.2974 10.5448 18.2978 10.2537 18.2191V18.2181C9.85746 18.1108 9.59965 17.839 9.43758 17.599C9.27623 17.36 9.15315 17.0703 9.04842 16.7749C8.83905 16.1842 8.62996 15.3461 8.3716 14.3135V14.3125C8.32287 14.1171 8.29666 14.0169 8.27306 13.9462C8.27238 13.9441 8.27171 13.9421 8.27107 13.9402C8.26992 13.9398 8.26831 13.9397 8.26709 13.9392C8.19746 13.9134 8.09824 13.8838 7.9048 13.8287H7.9038C7.01716 13.5751 6.28619 13.3673 5.76686 13.1609C5.50565 13.057 5.24523 12.9337 5.02734 12.7737C4.80497 12.6104 4.56776 12.3654 4.46499 12.0063V12.0053C4.37762 11.6987 4.37762 11.3734 4.46499 11.0667V11.0657L4.50978 10.9364C4.62696 10.6467 4.83277 10.4413 5.02734 10.2984C5.24523 10.1383 5.50565 10.015 5.76686 9.91118C6.28619 9.70471 7.01714 9.49701 7.9038 9.24332L8.26709 9.13284C8.26847 9.13233 8.26978 9.13134 8.27107 9.13085C8.27152 9.12954 8.2726 9.12826 8.27306 9.12687C8.29659 9.05618 8.32263 8.95638 8.3716 8.7596V8.7586L8.72792 7.36118C8.83852 6.94875 8.94375 6.5915 9.04842 6.29619C9.15315 6.00074 9.27615 5.71108 9.43758 5.47207C9.59972 5.23206 9.85767 4.95878 10.2547 4.85199L10.4767 4.80819ZM10.6618 6.8685C10.4867 7.36268 10.3008 8.10258 10.0328 9.17365C9.96005 9.46604 9.87841 9.86432 9.63366 10.1819L9.63167 10.1849C9.55326 10.2858 9.46375 10.3776 9.36493 10.4586L9.36393 10.4576C9.05572 10.7115 8.65922 10.8071 8.37359 10.8886L8.37459 10.8896C7.44827 11.1546 6.81882 11.3347 6.39888 11.5017C6.37093 11.5128 6.34557 11.5253 6.32125 11.5355C6.34567 11.5459 6.37079 11.5592 6.39888 11.5704C6.81882 11.7373 7.44826 11.9174 8.37459 12.1825H8.37359C8.62903 12.2552 8.96143 12.3366 9.24648 12.5249L9.36493 12.6125L9.36791 12.6154L9.50925 12.7468C9.55377 12.7931 9.59654 12.8411 9.63565 12.8921H9.63466C9.87752 13.2082 9.95977 13.6057 10.0328 13.8984L10.3821 15.267C10.4841 15.6477 10.5742 15.9565 10.6618 16.2036C10.6741 16.2383 10.6882 16.2699 10.6996 16.3001C10.7111 16.2698 10.7261 16.2384 10.7385 16.2036C10.9136 15.7094 11.0995 14.9695 11.3675 13.8984C11.4402 13.6061 11.522 13.2078 11.7666 12.8902L11.7686 12.8872L11.895 12.7428C11.9381 12.6986 11.9837 12.6568 12.0314 12.6174L12.1508 12.5279C12.4376 12.3366 12.7736 12.2544 13.0257 12.1825L14.2042 11.8391C14.5295 11.74 14.7917 11.6538 15.0014 11.5704C15.0291 11.5593 15.0539 11.5457 15.078 11.5355C15.054 11.5254 15.029 11.5127 15.0014 11.5017C14.7916 11.4183 14.5296 11.3311 14.2042 11.232L13.0257 10.8896C12.7349 10.8066 12.3427 10.7116 12.0354 10.4596L12.0324 10.4566C11.9345 10.3755 11.8462 10.2836 11.7686 10.1829V10.1839C11.5836 9.94538 11.4913 9.65879 11.4262 9.40854L11.3675 9.17365C11.0995 8.10255 10.9136 7.36267 10.7385 6.8685C10.726 6.83333 10.7112 6.80151 10.6996 6.77095C10.6881 6.80138 10.6742 6.8335 10.6618 6.8685ZM2.13993 8.96762V7.68366H0.855972C0.3832 7.68366 0 7.30046 0 6.82769C7.73956e-05 6.35498 0.383248 5.97172 0.855972 5.97172H2.13993V4.68776C2.13993 4.21503 2.52319 3.83186 2.9959 3.83179C3.46867 3.83179 3.85187 4.21499 3.85187 4.68776V5.97172H5.13583C5.60855 5.97172 5.99172 6.35498 5.9918 6.82769C5.9918 7.30046 5.6086 7.68366 5.13583 7.68366H3.85187V8.96762C3.85187 9.44039 3.46867 9.82359 2.9959 9.82359C2.52319 9.82351 2.13993 9.44034 2.13993 8.96762Z" fill="currentColor"/>
  </svg>
);

const DeleteIcon = ({ size = 16 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M3 6H5H21" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    <path d="M8 6V4C8 3.46957 8.21071 2.96086 8.58579 2.58579C8.96086 2.21071 9.46957 2 10 2H14C14.5304 2 15.0391 2.21071 15.4142 2.58579C15.7893 2.96086 16 3.46957 16 4V6M19 6V20C19 20.5304 18.7893 21.0391 18.4142 21.4142C18.0391 21.7893 17.5304 22 17 22H7C6.46957 22 5.96086 21.7893 5.58579 21.4142C5.21071 21.0391 5 20.5304 5 20V6H19Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

const CalendarIcon = ({ size = 18 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <rect x="3" y="4" width="18" height="18" rx="2" ry="2" stroke="white" strokeWidth="2"/>
    <line x1="16" y1="2" x2="16" y2="6" stroke="white" strokeWidth="2"/>
    <line x1="8" y1="2" x2="8" y2="6" stroke="white" strokeWidth="2"/>
    <line x1="3" y1="10" x2="21" y2="10" stroke="white" strokeWidth="2"/>
  </svg>
);

const ClockIcon = ({ size = 18 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2"/>
    <polyline points="12,6 12,12 16,14" stroke="currentColor" strokeWidth="2"/>
  </svg>
);

const TrashIcon = ({ size = 24 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
    <path d="M3 6H5H21" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    <path d="M8 6V4C8 3.46957 8.21071 2.96086 8.58579 2.58579C8.96086 2.21071 9.46957 2 10 2H14C14.5304 2 15.0391 2.21071 15.4142 2.58579C15.7893 2.96086 16 3.46957 16 4V6M19 6V20C19 20.5304 18.7893 21.0391 18.4142 21.4142C18.0391 21.7893 17.5304 22 17 22H7C6.46957 22 5.96086 21.7893 5.58579 21.4142C5.21071 21.0391 5 20.5304 5 20V6H19Z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    <line x1="10" y1="11" x2="10" y2="17" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
    <line x1="14" y1="11" x2="14" y2="17" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
  </svg>
);

// AI Loading Animation Component
const AILoadingAnimation = ({ text = '' }) => {
  return (
    <div className="ai-loading-container">
      <div className="ai-loading-content">
        <div className="ai-loading-icon-container">
          <AIIcon size={24} />
          <div className="ai-loading-pulse"></div>
        </div>
        <div className="ai-loading-text">
          {text}
          <span className="ai-loading-dots">
            <span className="dot">.</span>
            <span className="dot">.</span>
            <span className="dot">.</span>
          </span>
        </div>
      </div>
      <div className="ai-loading-background"></div>
    </div>
  );
};

const PostEditPage = ({
  post,
  onClose,
  onSave,
  onAIRegenerate,
  onAddImage,
  campaignId,
  isSaving: propIsSaving = false,
  onOpenPromptEditor,
  isLoading: propIsLoading = false,
  isActive = true
}) => {
  const { t } = useLanguage();
  const { notify, confirm } = useMobileAlert();
  const { registerSwipeBackGuard } = useMobileSwipeBackContext() || {};
  const unsupportedFormatMessage = useMemo(
    () => t('postReview.postEdit.media.unsupportedFormat', { defaultValue: `Formato non supportato. Usa: ${SUPPORTED_MEDIA_LABEL}` }),
    [t]
  );
  const [editedPost, setEditedPost] = useState(post);
  const [draggedIndex, setDraggedIndex] = useState(null);
  const [dragOverIndex, setDragOverIndex] = useState(null);
  const [showAddImageModal, setShowAddImageModal] = useState(false);
  const [addImageModalMethod, setAddImageModalMethod] = useState(null);
  const [showImageGallery, setShowImageGallery] = useState(false);
  const [showAIModal, setShowAIModal] = useState(false);
  const [aiModalType, setAiModalType] = useState('text');
  const [aiImageIndex, setAiImageIndex] = useState(null);
  const [videoThumbnails, setVideoThumbnails] = useState({});
  const [thumbnailsLoading, setThumbnailsLoading] = useState({});
  const [isAIGeneratingDescription, setIsAIGeneratingDescription] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const objectUrlRef = useRef(new Set());
  const fileInputRef = useRef(null);
  const [imageLoadingStates, setImageLoadingStates] = useState(() => new Map());
  const [isDatePickerOpen, setIsDatePickerOpen] = useState(false);
  const datePickerRef = useRef(null);
  const dateFieldRef = useRef(null);
  const [selectedDate, setSelectedDate] = useState(() => {
    const sourceDate = post?.scheduled_time || post?.scheduledDate;
    const parsed = sourceDate ? new Date(sourceDate) : new Date();
    return isNaN(parsed.getTime()) ? new Date() : parsed;
  });
  const [selectedTime, setSelectedTime] = useState(() => {
    const sourceDate = post?.scheduled_time || post?.scheduledDate;
    if (!sourceDate) return '12:00';
    const parsed = new Date(sourceDate);
    if (isNaN(parsed.getTime())) return '12:00';
    const hours = String(parsed.getHours()).padStart(2, '0');
    const minutes = String(parsed.getMinutes()).padStart(2, '0');
    return `${hours}:${minutes}`;
  });
  const [pickerMonth, setPickerMonth] = useState(() => {
    const sourceDate = post?.scheduled_time || post?.scheduledDate;
    const parsed = sourceDate ? new Date(sourceDate) : new Date();
    return isNaN(parsed.getTime()) ? new Date() : parsed;
  });
  const timeInputRef = useRef(null);
  const timePanelRef = useRef(null);
  const [isTimePickerOpen, setIsTimePickerOpen] = useState(false);

  const setImageLoadingState = useCallback((key, status) => {
    if (!key) return;
    setImageLoadingStates(prev => {
      const current = prev.get(key);
      if (current === status) {
        return prev;
      }
      const next = new Map(prev);
      next.set(key, status);
      return next;
    });
  }, [setImageLoadingStates]);

  const getImageLoadingProps = useCallback((source, scope = 'post-edit') => {
    if (!source) {
      return null;
    }
    const normalizedScope = scope || 'post-edit';
    const baseKey = typeof source === 'string' && source.length > 0
      ? source
      : 'unknown';
    const mapKey = `${normalizedScope}::${baseKey}`;
    const status = imageLoadingStates.get(mapKey) || 'loading';

    const statusClass = status === 'loaded'
      ? 'is-ready'
      : status === 'error'
        ? 'is-error'
        : 'is-loading';

    return {
      status,
      className: statusClass,
      onLoad: () => setImageLoadingState(mapKey, 'loaded'),
      onError: () => setImageLoadingState(mapKey, 'error')
    };
  }, [imageLoadingStates, setImageLoadingState]);

  const getPostIdentity = useCallback((postData) => {
    if (!postData) return null;

    return (
      postData.originalData?._id ||
      postData.originalData?.id ||
      postData._id ||
      postData.id ||
      postData.postId ||
      null
    );
  }, []);

  const imageLoadingScope = useMemo(
    () => `post-edit-${getPostIdentity(post || editedPost) || 'unknown'}`,
    [post, editedPost, getPostIdentity]
  );

  const createOriginalSnapshot = useCallback((sourcePost) => {
    if (!sourcePost) return null;

    const snapshotMediaUrls = Array.isArray(sourcePost.media_urls)
      ? [...sourcePost.media_urls]
      : Array.isArray(sourcePost.images)
        ? [...sourcePost.images]
        : [];

    const snapshotMediaArray = Array.isArray(sourcePost.mediaArray) && sourcePost.mediaArray.length > 0
      ? sourcePost.mediaArray.map(item => ({ ...item }))
      : buildMediaArrayFromUrls(snapshotMediaUrls);

    return {
      ...sourcePost,
      media_urls: snapshotMediaUrls,
      images: buildImagesFromMediaArray(snapshotMediaArray),
      mediaArray: snapshotMediaArray,
      content: sourcePost.content || sourcePost.description || '',
      description: sourcePost.content || sourcePost.description || ''
    };
  }, []);

  const revokePreviewUrl = useCallback((mediaItem) => {
    if (!mediaItem || mediaItem.type !== 'file') {
      return;
    }

    const previewUrl = mediaItem.previewUrl;
    if (previewUrl && previewUrl.startsWith('blob:')) {
      try {
        URL.revokeObjectURL(previewUrl);
      } catch (error) {
        console.warn('[PostEditPage] Failed to revoke object URL', error);
      }
      objectUrlRef.current.delete(previewUrl);
    }
  }, []);

  const createMediaItemFromFile = useCallback((file) => {
    if (!(file instanceof File)) {
      return null;
    }

    const previewUrl = URL.createObjectURL(file);
    objectUrlRef.current.add(previewUrl);

    return {
      id: generateMediaId(),
      type: 'file',
      file,
      previewUrl,
      name: file.name
    };
  }, []);

  const createMediaItemFromUrl = useCallback((url, previewUrl) => {
    const resolvedUrl = toAbsoluteMediaUrl(url);

    if (!resolvedUrl) {
      return null;
    }

    return {
      id: generateMediaId(),
      type: 'url',
      url: resolvedUrl,
      previewUrl: previewUrl || resolvedUrl
    };
  }, []);

  const addMediaItemsToPost = useCallback((mediaItems) => {
    if (!mediaItems || mediaItems.length === 0) {
      return;
    }

    setEditedPost(prev => {
      if (!prev) {
        return prev;
      }

      const existingMediaArray = prev.mediaArray ? [...prev.mediaArray] : [];
      const sanitizedNewItems = mediaItems.filter(Boolean);
      const updatedMediaArray = [...existingMediaArray, ...sanitizedNewItems];

      return {
        ...prev,
        mediaArray: updatedMediaArray,
        images: buildImagesFromMediaArray(updatedMediaArray),
        __hasPendingRegeneratedChanges: true
      };
    });
  }, []);

  const extractRegeneratedImageUrls = useCallback((response = {}) => {
    const urls = new Set();
    const pushUrl = (candidate) => {
      if (!candidate || typeof candidate !== 'string') {
        return;
      }
      const resolved = toAbsoluteMediaUrl(candidate) || candidate;
      if (resolved) {
        urls.add(resolved);
      }
    };

    if (Array.isArray(response.regenerated_images)) {
      response.regenerated_images.forEach(pushUrl);
    }

    if (Array.isArray(response?.regenerated_content?.media_urls)) {
      response.regenerated_content.media_urls.forEach(pushUrl);
    }

    if (response?.image_url) {
      pushUrl(response.image_url);
    }

    const metadataImages = response?.regeneration_metadata?.generated_images;
    if (Array.isArray(metadataImages)) {
      metadataImages.forEach((item) => {
        pushUrl(item?.url || item?.image_url || item?.public_url || item?.source_url);
      });
    }

    return Array.from(urls);
  }, []);

  // Skeleton page while loading
  const renderSkeletonPage = useCallback(() => {
    return (
      <div className="post-edit-page">
        <div className="post-edit-content">
          <div className="post-edit-header" style={{ gap: 16 }}>
            <Skeleton variant="circle" width="40px" height="40px" shimmer />
            <Skeleton width="50%" height="20px" shimmer />
            <Skeleton variant="circle" width="40px" height="40px" shimmer />
          </div>

          <div className="post-edit-section" style={{ marginTop: 24 }}>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8, marginBottom: 16 }}>
              <Skeleton width="45%" height="18px" shimmer />
              <Skeleton width="80%" height="14px" shimmer />
            </div>

            <div className="post-edit-images-grid" style={{ gap: 12 }}>
              {Array.from({ length: 6 }).map((_, idx) => (
                <div key={`skeleton-image-${idx}`} className="post-edit-image-item" style={{ aspectRatio: '4 / 5' }}>
                  <Skeleton variant="rounded" width="100%" height="100%" shimmer />
                </div>
              ))}
            </div>
          </div>

          <div className="post-edit-section" style={{ marginTop: 24 }}>
            <Skeleton width="40%" height="18px" shimmer />
            <div style={{ display: 'flex', flexDirection: 'column', gap: 10, marginTop: 12 }}>
              {Array.from({ length: 3 }).map((_, idx) => (
                <Skeleton key={`skeleton-text-${idx}`} width="100%" height="14px" shimmer />
              ))}
            </div>
          </div>

          <div style={{ marginTop: 32, display: 'flex', flexDirection: 'column', gap: 12 }}>
            <Skeleton variant="rounded" width="100%" height="48px" shimmer />
            <Skeleton variant="rounded" width="100%" height="48px" shimmer />
          </div>
        </div>
      </div>
    );
  }, []);

  // Helper function to validate post data
  const validatePostData = useCallback((postData) => {
    if (!postData) {
      console.warn('[PostEditPage] No post data provided');
      return false;
    }
    
    const hasId = postData.id || postData._id;
    const hasContent = postData.content || postData.description;
    
    console.log('[PostEditPage] Post validation:', {
      hasId: !!hasId,
      hasContent: !!hasContent,
      hasImages: !!(postData.images || postData.media_urls),
      platform: postData.platform,
      scheduledTime: postData.scheduled_time || postData.scheduledDate
    });
    
    return !!hasId;
  }, []);

  // Helper function to check if URL is video
  const isVideoFile = useCallback((url) => {
    if (!url) return false;
    const videoExtensions = ['.mp4', '.mov', '.avi', '.webm', '.ogg', '.mkv', '.m4v'];
    const urlLowerCase = url.toLowerCase();
    return videoExtensions.some(ext => urlLowerCase.includes(ext));
  }, []);

  // Helper function to generate thumbnail from video first frame
  const generateVideoThumbnail = useCallback((videoUrl) => {
    return new Promise((resolve, reject) => {
      const video = document.createElement('video');
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      
      video.crossOrigin = 'anonymous';
      video.muted = true;
      video.playsInline = true;
      
      const handleLoadedData = () => {
        try {
          // Set canvas size for edit grid thumbnails (smaller than PostInfo)
          const aspectRatio = video.videoWidth / video.videoHeight;
          canvas.width = 120; // Fixed thumbnail width for grid
          canvas.height = 120 / aspectRatio;
          
          // Draw first frame to canvas
          ctx.drawImage(video, 0, 0, canvas.width, canvas.height);
          
          // Convert canvas to data URL
          const thumbnailDataUrl = canvas.toDataURL('image/jpeg', 0.8);
          resolve(thumbnailDataUrl);
        } catch (error) {
          reject(error);
        } finally {
          // Cleanup
          video.removeEventListener('loadeddata', handleLoadedData);
          video.removeEventListener('error', handleError);
          video.src = '';
          video.load();
        }
      };
      
      const handleError = (error) => {
        video.removeEventListener('loadeddata', handleLoadedData);
        video.removeEventListener('error', handleError);
        reject(new Error('Failed to load video for thumbnail generation'));
      };
      
      video.addEventListener('loadeddata', handleLoadedData);
      video.addEventListener('error', handleError);
      
      // Load video and seek to first frame
      video.src = videoUrl;
      video.currentTime = 0.1; // Slightly after start to ensure frame is loaded
    });
  }, []);

  // Initialize edited post data
  useEffect(() => {
    if (!post || !validatePostData(post)) {
      console.warn('[PostEditPage] No valid post provided for editing');
      setEditedPost(null);
      return;
    }

    setEditedPost(prev => {
      const incomingPostId = getPostIdentity(post);
      const previousPostId = prev ? getPostIdentity(prev) : null;
      const isSamePost = Boolean(prev && incomingPostId && previousPostId && incomingPostId === previousPostId);

      const initialMediaUrls = post.media_urls || post.images || [];
      const mediaArray = (Array.isArray(post.mediaArray) && post.mediaArray.length > 0)
        ? post.mediaArray.map(item => ({ ...item }))
        : buildMediaArrayFromUrls(initialMediaUrls);

      if (isSamePost) {
        const mergedPost = {
          ...prev,
          ...post,
          images: buildImagesFromMediaArray(mediaArray),
          media_urls: initialMediaUrls,
          mediaArray,
          description: post.content || post.description || prev?.description || '',
          content: post.content || post.description || prev?.content || '',
          scheduled_time: post.scheduled_time || post.scheduledDate || prev?.scheduled_time || null,
          platform: post.platform || prev?.platform || 'instagram',
          hashtags: post.hashtags || prev?.hashtags || [],
          mentions: post.mentions || prev?.mentions || []
        };

        mergedPost.originalData = prev?.originalData || createOriginalSnapshot(post.originalData || post);

        console.log('[PostEditPage] Merged post update into existing state:', {
          incomingPostId,
          mediaCount: mergedPost.mediaArray?.length,
          hasPendingRegeneratedChanges: mergedPost.__hasPendingRegeneratedChanges
        });

        return mergedPost;
      }

      console.log('[PostEditPage] Initializing new post for editing:', {
        incomingPostId,
        mediaCount: mediaArray.length
      });

      return {
        ...post,
        images: buildImagesFromMediaArray(mediaArray),
        description: post.content || post.description || '',
        content: post.content || post.description || '',
        media_urls: initialMediaUrls,
        mediaArray,
        originalData: createOriginalSnapshot(post.originalData || post),
        scheduled_time: post.scheduled_time || post.scheduledDate || null,
        platform: post.platform || 'instagram',
        hashtags: post.hashtags || [],
        mentions: post.mentions || []
      };
    });
  }, [post, validatePostData, getPostIdentity, createOriginalSnapshot]);

  // Generate thumbnails for video files
  useEffect(() => {
    const generateThumbnails = async () => {
      if (!editedPost?.images || editedPost.images.length === 0) return;

      for (let i = 0; i < editedPost.images.length; i++) {
        const mediaItem = editedPost.mediaArray?.[i];
        const displaySource = mediaItem?.previewUrl || mediaItem?.url || editedPost.images[i];
        const thumbnailKey = `${mediaItem?.id || displaySource}-${i}`;
        const isVideo = mediaItem?.type === 'file'
          ? !!mediaItem.file && mediaItem.file.type.startsWith('video/')
          : isVideoFile(mediaItem?.url || displaySource);

        if (isVideo && displaySource && !videoThumbnails[thumbnailKey] && !thumbnailsLoading[thumbnailKey]) {
          setThumbnailsLoading(prev => ({ ...prev, [thumbnailKey]: true }));

          try {
            const thumbnail = await generateVideoThumbnail(displaySource);
            setVideoThumbnails(prev => ({ ...prev, [thumbnailKey]: thumbnail }));
          } catch (error) {
            console.error('[PostEditPage] Error generating video thumbnail:', error);
          } finally {
            setThumbnailsLoading(prev => ({ ...prev, [thumbnailKey]: false }));
          }
        }
      }
    };

    generateThumbnails();
  }, [editedPost?.images, editedPost?.mediaArray, isVideoFile, generateVideoThumbnail, videoThumbnails, thumbnailsLoading]);

  const clearPendingChangesFlag = useCallback(() => {
    setEditedPost(prev => {
      if (!prev?.__hasPendingRegeneratedChanges) {
        return prev;
      }
      return {
        ...prev,
        __hasPendingRegeneratedChanges: false
      };
    });
  }, []);

  const getUnsavedChangesStatus = useCallback(() => {
    if (!editedPost) {
      return {
        hasChanges: false,
        mediaChanged: false,
        contentChanged: false,
        scheduleChanged: false,
        confirmationMessage: null
      };
    }

    const original = editedPost.originalData || post || {};
    const originalContent = original.content || original.description || '';
    const currentContent = editedPost.description || editedPost.content || '';
    const contentChanged = originalContent !== currentContent;

    const originalMedia = original.media_urls || original.images || [];
    const currentMedia = editedPost.images || [];
    const mediaChanged = JSON.stringify(originalMedia) !== JSON.stringify(currentMedia);

    const normalizeDate = (value) => {
      if (!value) return '';
      const d = new Date(value);
      return isNaN(d.getTime()) ? '' : d.toISOString();
    };
    const originalSchedule = normalizeDate(original.scheduled_time || original.scheduledDate);
    const currentSchedule = normalizeDate(editedPost.scheduled_time || editedPost.scheduledDate);
    const scheduleChanged = originalSchedule !== currentSchedule;

    let confirmationMessage = t('postReview.postEdit.alerts.leaveDefault', {
      defaultValue: 'Hai modifiche non salvate. Vuoi uscire senza salvare?'
    });

    if (mediaChanged && contentChanged && scheduleChanged) {
      confirmationMessage = t('postReview.postEdit.alerts.leaveMediaTextTime', {
        defaultValue: 'Hai modificato testo, immagini e orario senza salvare. Vuoi davvero uscire?'
      });
    } else if ((mediaChanged && contentChanged) || (mediaChanged && scheduleChanged) || (contentChanged && scheduleChanged)) {
      confirmationMessage = t('postReview.postEdit.alerts.leaveMultiple', {
        defaultValue: 'Hai più modifiche non salvate. Vuoi davvero uscire?'
      });
    } else if (mediaChanged) {
      confirmationMessage = t('postReview.postEdit.alerts.leaveMediaOnly', {
        defaultValue: 'Hai modificato le immagini senza salvare. Vuoi davvero uscire?'
      });
    } else if (contentChanged) {
      confirmationMessage = t('postReview.postEdit.alerts.leaveTextOnly', {
        defaultValue: 'Hai rigenerato la descrizione senza salvare. Vuoi davvero uscire?'
      });
    } else if (scheduleChanged) {
      confirmationMessage = t('postReview.postEdit.alerts.leaveScheduleOnly', {
        defaultValue: 'Hai modificato la data/ora senza salvare. Vuoi davvero uscire?'
      });
    }

    const hasChanges = mediaChanged || contentChanged || scheduleChanged || editedPost.__hasPendingRegeneratedChanges;

    return {
      hasChanges,
      mediaChanged,
      contentChanged,
      scheduleChanged,
      confirmationMessage
    };
  }, [editedPost, post, t]);

  const confirmLeaveIfNeeded = useCallback(async () => {
    const { hasChanges, confirmationMessage } = getUnsavedChangesStatus();

    if (!hasChanges) {
      clearPendingChangesFlag();
      return true;
    }

    const shouldExit = await confirm({
      title: confirmationMessage,
      variant: 'warning',
      confirmText: t('postReview.postEdit.actions.leave', { defaultValue: 'Esci senza salvare' }),
      cancelText: t('common.actions.cancel', { defaultValue: 'Annulla' })
    });

    if (shouldExit) {
      clearPendingChangesFlag();
    }

    return Boolean(shouldExit);
  }, [confirm, getUnsavedChangesStatus, t, clearPendingChangesFlag]);

  useEffect(() => {
    if (!editedPost?.__hasPendingRegeneratedChanges) {
      return;
    }

    const { hasChanges } = getUnsavedChangesStatus();
    if (!hasChanges) {
      clearPendingChangesFlag();
    }
  }, [editedPost, getUnsavedChangesStatus, clearPendingChangesFlag]);

  const handleCloseAttempt = useCallback(async () => {
    const shouldExit = await confirmLeaveIfNeeded();
    if (!shouldExit) {
      return;
    }

    if (typeof onClose === 'function') {
      onClose();
    }
  }, [confirmLeaveIfNeeded, onClose]);

  useEffect(() => {
    if (typeof registerSwipeBackGuard !== 'function') {
      return undefined;
    }

    if (!isActive) {
      return registerSwipeBackGuard(null);
    }

    const unregister = registerSwipeBackGuard(confirmLeaveIfNeeded);
    return () => {
      if (typeof unregister === 'function') {
        unregister();
      }
    };
  }, [registerSwipeBackGuard, confirmLeaveIfNeeded, isActive]);

  // Handle escape key
  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key !== 'Escape') {
        return;
      }

      if (isDatePickerOpen) {
        e.stopPropagation();
        setIsDatePickerOpen(false);
        return;
      }

      if (!isSaving) {
        handleCloseAttempt();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [handleCloseAttempt, isDatePickerOpen, isSaving]);

  useEffect(() => () => {
    objectUrlRef.current.forEach((url) => {
      if (typeof url === 'string' && url.startsWith('blob:')) {
        try {
          URL.revokeObjectURL(url);
        } catch (error) {
          console.warn('[PostEditPage] Failed to revoke object URL on unmount', error);
        }
      }
    });
    objectUrlRef.current.clear();
  }, []);

  // Debug: Log when editedPost description changes
  useEffect(() => {
    if (editedPost?.description) {
      console.log('[PostEditPage] EditedPost description changed:', editedPost.description.substring(0, 100) + '...');
    }
  }, [editedPost?.description]);
  
  // Debug: Log when isAIGeneratingDescription changes
  useEffect(() => {
    console.log('[PostEditPage] isAIGeneratingDescription changed to:', isAIGeneratingDescription);
  }, [isAIGeneratingDescription]);

  // Monitor image changes for debugging - Removed for production
  // useEffect(() => {
  //   console.log('[PostEditPage] Images changed:', {
  //     length: editedPost.images?.length,
  //     images: editedPost.images?.map((img, i) => `${i}: ${img.substring(0, 20)}...`)
  //   });
  // }, [editedPost.images]);

  // Simplified Drag and Drop System
  const [isDragging, setIsDragging] = useState(false);
  const isDraggingRef = useRef(false); // Ref sincrono per logica immediata
  const [dragStartPos, setDragStartPos] = useState({ x: 0, y: 0 });
  const [dragCurrentPos, setDragCurrentPos] = useState({ x: 0, y: 0 });

  const [dragGridBounds, setDragGridBounds] = useState(null);
  const [forceRenderKey, setForceRenderKey] = useState(0);
  const [lastValidTarget, setLastValidTarget] = useState(null);
  const [draggedImageDimensions, setDraggedImageDimensions] = useState({ width: 0, height: 0 });
  const [pressedImageIndex, setPressedImageIndex] = useState(null);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteImageIndex, setDeleteImageIndex] = useState(null);
  const [isOverTrash, setIsOverTrash] = useState(false);

  // Lock body scroll while dragging to avoid accidental page scroll during drag & drop
  useEffect(() => {
    if (!isDragging || typeof window === 'undefined' || typeof document === 'undefined') {
      return undefined;
    }

    const body = document.body;
    if (!body) {
      return undefined;
    }

    const previousOverflow = body.style.overflow;
    const previousTouchAction = body.style.touchAction;
    const previousOverscrollBehavior = body.style.overscrollBehavior;

    const preventScroll = (event) => {
      if (event.touches?.length > 1) {
        return;
      }
      event.preventDefault();
    };

    body.style.overflow = 'hidden';
    body.style.touchAction = 'none';
    body.style.overscrollBehavior = 'contain';

    window.addEventListener('touchmove', preventScroll, { passive: false });
    window.addEventListener('wheel', preventScroll, { passive: false });

    return () => {
      body.style.overflow = previousOverflow;
      body.style.touchAction = previousTouchAction;
      body.style.overscrollBehavior = previousOverscrollBehavior;
      window.removeEventListener('touchmove', preventScroll);
      window.removeEventListener('wheel', preventScroll);
    };
  }, [isDragging]);
  
  // API integration for post editing
  const api = useApi();
  const {
    clearError: apiClearError
  } = useMobilePostReview(campaignId);

  // Initialize postService with API hook on mount
  useEffect(() => {
    if (api) {
      postService.initialize(api);
    }
  }, [api]);

  const startDragMode = useCallback((index, startX, startY) => {
    
    // Get grid bounds for constraining drag first
    const gridElement = document.querySelector('.post-edit-images-grid');
    if (gridElement) {
      const rect = gridElement.getBoundingClientRect();
      setDragGridBounds(rect);
    }
    
    // Capture the real dimensions of the image being dragged
    const imageContainer = document.querySelector(`[data-image-index="${index}"]`);
    if (imageContainer) {
      const rect = imageContainer.getBoundingClientRect();
      setDraggedImageDimensions({ 
        width: rect.width, 
        height: rect.height 
      });
    }
    
    // Set all drag state synchronously
    setDraggedIndex(index);
    setDragStartPos({ x: startX, y: startY });
    setDragCurrentPos({ x: startX, y: startY });
    setDragOverIndex(null);
    setLastValidTarget(null);
    isDraggingRef.current = true; // Set ref immediatamente (sincrono)
    setIsDragging(true); // Set state per UI (asincrono)
    

  }, []);

  const handleDragComplete = useCallback((finalTargetIndex = null, droppedOnDelete = false, sourceDraggedIndex = null) => {
    // Use passed index or fallback to state
    const currentDraggedIndex = sourceDraggedIndex !== null ? sourceDraggedIndex : draggedIndex;
    
    // Check if dropped on delete zone
    if (currentDraggedIndex !== null && (droppedOnDelete || isOverTrash)) {
      setDeleteImageIndex(currentDraggedIndex);
      setShowDeleteConfirm(true);
    } else {
      // Normal swap logic
      const targetIndex = finalTargetIndex !== null ? finalTargetIndex : 
                         (dragOverIndex !== null ? dragOverIndex : lastValidTarget);
      
      if (currentDraggedIndex !== null && targetIndex !== null && currentDraggedIndex !== targetIndex) {
        
        setEditedPost(prev => {
          if (!prev?.mediaArray || !Array.isArray(prev.mediaArray)) {
            return prev;
          }

          const mediaLength = prev.mediaArray.length;
          if (
            currentDraggedIndex < 0 ||
            currentDraggedIndex >= mediaLength ||
            targetIndex < 0 ||
            targetIndex >= mediaLength
          ) {
            console.error('[PostEditPage] Invalid indices for swap:', {
              currentDraggedIndex,
              targetIndex,
              arrayLength: mediaLength
            });
            return prev;
          }

          const newMediaArray = [...prev.mediaArray];
          const [movedItem] = newMediaArray.splice(currentDraggedIndex, 1);
          newMediaArray.splice(targetIndex, 0, movedItem);

          return {
            ...prev,
            mediaArray: newMediaArray,
            images: buildImagesFromMediaArray(newMediaArray)
          };
        });
        
        // Force re-render to ensure images are updated visually
        setForceRenderKey(prev => prev + 1);
      } else {
      }
    }
    
    // Reset drag state
    isDraggingRef.current = false; // Reset ref immediatamente (sincrono)
    setIsDragging(false);
    setDraggedIndex(null);
    setDragOverIndex(null);
    setLastValidTarget(null);
    setDragStartPos({ x: 0, y: 0 });
    setDragCurrentPos({ x: 0, y: 0 });
    setDragGridBounds(null);
    setDraggedImageDimensions({ width: 0, height: 0 });
    setPressedImageIndex(null);
    setIsOverTrash(false);
    
    // Clean up any remaining CSS classes
    const containers = document.querySelectorAll('.post-edit-image-container');
    containers.forEach(container => {
      container.classList.remove('being-dragged');
    });
    
    const items = document.querySelectorAll('.post-edit-image-item');
    items.forEach(item => {
      item.classList.remove('drag-target', 'dragging');
    });
  }, [draggedIndex, dragOverIndex, lastValidTarget, isOverTrash]);

  const handleImageMouseDown = useCallback((e, index) => {
    // Don't interfere with button clicks
    if (e.target.closest('button')) {
      return;
    }

    e.preventDefault();
    
    // Immediate visual feedback
    setPressedImageIndex(index);
    
    const startX = e.clientX;
    const startY = e.clientY;
    let hasMoved = false;
    let hasStartedDrag = false;
    const DRAG_THRESHOLD = 1; // Ridotto a 1 pixel per drag immediato

    // Inizializza il drag mode immediatamente
    startDragMode(index, startX, startY);
    hasStartedDrag = true;

    // Mouse move handler
    const handleMouseMove = (moveEvent) => {
      const deltaX = Math.abs(moveEvent.clientX - startX);
      const deltaY = Math.abs(moveEvent.clientY - startY);
      const distance = Math.sqrt(deltaX * deltaX + deltaY * deltaY);
      
      // Mark as moved for any movement, even small ones
      if (distance > 0) {
        hasMoved = true;
      }
      
      // Il drag è già iniziato, gestiamo il movimento usando il ref sincrono
      if (hasStartedDrag && isDraggingRef.current) {
        // Constrain to grid bounds
        let newX = moveEvent.clientX;
        let newY = moveEvent.clientY;
        
        if (dragGridBounds) {
          newX = Math.max(dragGridBounds.left, Math.min(dragGridBounds.right, newX));
          newY = Math.max(dragGridBounds.top, Math.min(dragGridBounds.bottom, newY));
        }
        
        setDragCurrentPos({ x: newX, y: newY });
        
        // Check for drop target (temporarily hide drag ghost to avoid interference)
        const dragGhost = document.querySelector('.post-edit-drag-ghost');
        if (dragGhost) dragGhost.style.display = 'none';
        
        const elementBelow = document.elementFromPoint(newX, newY);
        const imageItem = elementBelow?.closest('.post-edit-image-item');
        const trashArea = elementBelow?.closest('[data-delete-zone="true"]');
        
        if (dragGhost) dragGhost.style.display = '';
        
        if (trashArea) {
          // Over delete zone (transformed add image button)
          setIsOverTrash(true);
          setDragOverIndex(null);
        } else if (imageItem && !imageItem.classList.contains('post-edit-add-image-item')) {
          // Over another image (but not the add image button)
          const targetIndex = parseInt(imageItem.dataset.index);
          
          if (!isNaN(targetIndex) && targetIndex !== index) {
            setDragOverIndex(targetIndex);
            setLastValidTarget(targetIndex);
            setIsOverTrash(false); // Reset trash state
          }
        } else {
          // Over nothing or over add image area (but not delete zone)
          // Don't reset to null, keep the last valid target
          setIsOverTrash(false);
        }
      }
    };

    // Mouse up handler
    const handleMouseUp = (upEvent) => {
      if (!hasMoved) {
        // It's a normal click without movement - stop drag and no action needed
        handleDragComplete(null, false, index);
      } else if (hasStartedDrag) {
        // Handle drag end - capture final target at drop point
        
        // Hide drag ghost temporarily to ensure clean detection
        const dragGhost = document.querySelector('.post-edit-drag-ghost');
        if (dragGhost) dragGhost.style.display = 'none';
        
        // Get the element at the drop position
        const elementBelow = document.elementFromPoint(upEvent.clientX, upEvent.clientY);
        
        const imageItem = elementBelow?.closest('.post-edit-image-item');
        const deleteZone = elementBelow?.closest('[data-delete-zone="true"]');
        
        let finalTargetIndex = null;
        let droppedOnDelete = false;
        
        // Restore drag ghost
        if (dragGhost) dragGhost.style.display = '';
        
        if (deleteZone) {
          droppedOnDelete = true;
        } else if (imageItem && !imageItem.classList.contains('post-edit-add-image-item')) {
          const targetIndex = parseInt(imageItem.dataset.index);
          if (!isNaN(targetIndex) && targetIndex !== index) {
            finalTargetIndex = targetIndex;
          }
        }
        
        // Fallback: use tracked values during drag
        if (finalTargetIndex === null && !droppedOnDelete) {
          if (isOverTrash) {
            droppedOnDelete = true;
          } else if (dragOverIndex !== null && dragOverIndex !== index) {
            finalTargetIndex = dragOverIndex;
          }
        }
        
        handleDragComplete(finalTargetIndex, droppedOnDelete, index);
      }
      
      // Cleanup and reset pressed state
      setPressedImageIndex(null);
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
    };

    document.addEventListener('mousemove', handleMouseMove);
    document.addEventListener('mouseup', handleMouseUp);
  }, [draggedIndex, dragGridBounds, handleDragComplete]);

  // Touch handlers for mobile
  const handleImageTouchStart = useCallback((e, index) => {
    // Don't interfere with button clicks
    if (e.target.closest('button')) {
      return;
    }

    e.preventDefault();
    
    // Immediate visual feedback
    setPressedImageIndex(index);
    
    const touch = e.touches[0];
    const startX = touch.clientX;
    const startY = touch.clientY;
    let hasMoved = false;
    let hasStartedDrag = false;
    const DRAG_THRESHOLD = 1; // Ridotto a 1 pixel per drag immediato

    // Inizializza il drag mode immediatamente
    startDragMode(index, startX, startY);
    hasStartedDrag = true;

    // Touch move handler
    const handleTouchMove = (moveEvent) => {
      if (!moveEvent.touches[0]) return;
      
      const touch = moveEvent.touches[0];
      const deltaX = Math.abs(touch.clientX - startX);
      const deltaY = Math.abs(touch.clientY - startY);
      const distance = Math.sqrt(deltaX * deltaX + deltaY * deltaY);
      
      // Mark as moved for any movement, even small ones
      if (distance > 0) {
        hasMoved = true;
      }
      
      // Il drag è già iniziato, gestiamo il movimento usando il ref sincrono
      if (hasStartedDrag && isDraggingRef.current) {
        // Constrain to grid bounds
        let newX = touch.clientX;
        let newY = touch.clientY;
        
        if (dragGridBounds) {
          newX = Math.max(dragGridBounds.left, Math.min(dragGridBounds.right, newX));
          newY = Math.max(dragGridBounds.top, Math.min(dragGridBounds.bottom, newY));
        }
        
        setDragCurrentPos({ x: newX, y: newY });
        
        // Check for drop target (temporarily hide drag ghost to avoid interference)
        const dragGhost = document.querySelector('.post-edit-drag-ghost');
        if (dragGhost) dragGhost.style.display = 'none';
        
        const elementBelow = document.elementFromPoint(newX, newY);
        const imageItem = elementBelow?.closest('.post-edit-image-item');
        const trashArea = elementBelow?.closest('[data-delete-zone="true"]');
        
        if (dragGhost) dragGhost.style.display = '';
        
        if (trashArea) {
          // Over delete zone (transformed add image button)
          setIsOverTrash(true);
          setDragOverIndex(null);
        } else if (imageItem && !imageItem.classList.contains('post-edit-add-image-item')) {
          // Over another image (but not the add image button)
          const targetIndex = parseInt(imageItem.dataset.index);
          
          if (!isNaN(targetIndex) && targetIndex !== index) {
            setDragOverIndex(targetIndex);
            setLastValidTarget(targetIndex);
          }
        } else {
          // Over nothing or over add image area (but not delete zone)
          // Don't reset to null, keep the last valid target
        }
      }
    };

    // Touch end handler
    const handleTouchEnd = (endEvent) => {
      if (!hasMoved) {
        // It's a normal tap without movement - stop drag and no action needed
        handleDragComplete(null, false, index);
      } else if (hasStartedDrag) {
        // Handle drag end - capture final target at drop point
        
        // Get the last touch position if available
        const touch = endEvent.changedTouches && endEvent.changedTouches[0];
        let finalTargetIndex = null;
        let droppedOnDelete = false;
        
        if (touch) {
          // Hide drag ghost temporarily to ensure clean detection
          const dragGhost = document.querySelector('.post-edit-drag-ghost');
          if (dragGhost) dragGhost.style.display = 'none';
          
          // Get the element at the drop position
          const elementBelow = document.elementFromPoint(touch.clientX, touch.clientY);
          const imageItem = elementBelow?.closest('.post-edit-image-item');
          const deleteZone = elementBelow?.closest('[data-delete-zone="true"]');
          
          // Restore drag ghost
          if (dragGhost) dragGhost.style.display = '';
          
          if (deleteZone) {
            droppedOnDelete = true;
          } else if (imageItem && !imageItem.classList.contains('post-edit-add-image-item')) {
            const targetIndex = parseInt(imageItem.dataset.index);
            if (!isNaN(targetIndex) && targetIndex !== index) {
              finalTargetIndex = targetIndex;
            }
          }
          
        } else {
          // Fallback to last known drag position
          
          // Hide drag ghost temporarily to ensure clean detection
          const dragGhost = document.querySelector('.post-edit-drag-ghost');
          if (dragGhost) dragGhost.style.display = 'none';
          
          const elementBelow = document.elementFromPoint(dragCurrentPos.x, dragCurrentPos.y);
          const imageItem = elementBelow?.closest('.post-edit-image-item');
          const deleteZone = elementBelow?.closest('[data-delete-zone="true"]');
          
          // Restore drag ghost
          if (dragGhost) dragGhost.style.display = '';
          
          if (deleteZone) {
            droppedOnDelete = true;
          } else if (imageItem && !imageItem.classList.contains('post-edit-add-image-item')) {
            const targetIndex = parseInt(imageItem.dataset.index);
            if (!isNaN(targetIndex) && targetIndex !== index) {
              finalTargetIndex = targetIndex;
            }
          }
          
        }
        
        handleDragComplete(finalTargetIndex, droppedOnDelete, index);
      }
      
      // Cleanup and reset pressed state
      setPressedImageIndex(null);
      document.removeEventListener('touchmove', handleTouchMove);
      document.removeEventListener('touchend', handleTouchEnd);
    };

    document.addEventListener('touchmove', handleTouchMove, { passive: false });
    document.addEventListener('touchend', handleTouchEnd, { passive: false });
  }, [draggedIndex, dragGridBounds, dragCurrentPos, handleDragComplete]);


  // Image management
  const handleDeleteImage = useCallback((index) => {
    setEditedPost(prev => {
      if (!prev?.mediaArray || index < 0 || index >= prev.mediaArray.length) {
        return prev;
      }

      const newMediaArray = [...prev.mediaArray];
      const [removedItem] = newMediaArray.splice(index, 1);
      if (removedItem) {
        revokePreviewUrl(removedItem);
      }

      return {
        ...prev,
        mediaArray: newMediaArray,
        images: buildImagesFromMediaArray(newMediaArray),
        __hasPendingRegeneratedChanges: true
      };
    });
  }, [buildImagesFromMediaArray, revokePreviewUrl]);

  // Delete confirmation handlers
  const handleConfirmDelete = useCallback(() => {
    if (deleteImageIndex !== null) {
      handleDeleteImage(deleteImageIndex);
    }
    setShowDeleteConfirm(false);
    setDeleteImageIndex(null);
  }, [deleteImageIndex, handleDeleteImage]);

  const handleCancelDelete = useCallback(() => {
    setShowDeleteConfirm(false);
    setDeleteImageIndex(null);
  }, []);

  const handleAIRegenerateImage = useCallback((index) => {
    // In assenza di metadati, consentiamo l'accesso e mostriamo blocchi mock
    if (typeof onOpenPromptEditor === 'function') {
      onOpenPromptEditor(index, editedPost);
    }
  }, [onOpenPromptEditor, editedPost]);

  // Text management
  const handleTextChange = useCallback((newText) => {
    setEditedPost(prev => ({
      ...prev,
      description: newText,
      __hasPendingRegeneratedChanges: true
    }));
  }, []);

  // Date and Time management
  const getDateComponents = useCallback((dateString) => {
    if (!dateString) {
      const now = new Date();
      return {
        day: now.getDate().toString().padStart(2, '0'),
        month: (now.getMonth() + 1).toString().padStart(2, '0'),
        year: now.getFullYear().toString()
      };
    }
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) {
        const now = new Date();
        return {
          day: now.getDate().toString().padStart(2, '0'),
          month: (now.getMonth() + 1).toString().padStart(2, '0'),
          year: now.getFullYear().toString()
        };
      }
      return {
        day: date.getDate().toString().padStart(2, '0'),
        month: (date.getMonth() + 1).toString().padStart(2, '0'),
        year: date.getFullYear().toString()
      };
    } catch (error) {
      console.error('[PostEditPage] Error getting date components:', error);
      const now = new Date();
      return {
        day: now.getDate().toString().padStart(2, '0'),
        month: (now.getMonth() + 1).toString().padStart(2, '0'),
        year: now.getFullYear().toString()
      };
    }
  }, []);

  const formatTimeForInput = useCallback((dateString) => {
    if (!dateString) return '12:00';
    try {
      const date = new Date(dateString);
      if (isNaN(date.getTime())) return '12:00';
      // Return HH:MM format for time input
      const hours = date.getHours().toString().padStart(2, '0');
      const minutes = date.getMinutes().toString().padStart(2, '0');
      return `${hours}:${minutes}`;
    } catch (error) {
      console.error('[PostEditPage] Error formatting time for input:', error);
      return '12:00';
    }
  }, []);

  const isSameDay = useCallback((dateA, dateB) => {
    if (!dateA || !dateB) return false;
    return (
      dateA.getFullYear() === dateB.getFullYear() &&
      dateA.getMonth() === dateB.getMonth() &&
      dateA.getDate() === dateB.getDate()
    );
  }, []);

  const combineDateAndTime = useCallback((dateValue, timeValue) => {
    if (!dateValue || !timeValue) return null;
    const [hours, minutes] = timeValue.split(':').map(Number);
    if (Number.isNaN(hours) || Number.isNaN(minutes)) {
      return null;
    }

    const combinedDate = new Date(dateValue);
    combinedDate.setHours(hours, minutes, 0, 0);

    if (isNaN(combinedDate.getTime())) {
      return null;
    }

    setEditedPost(prev => {
      if (!prev) {
        return prev;
      }
      return {
        ...prev,
        scheduled_time: combinedDate.toISOString(),
        scheduledDate: combinedDate.toISOString(),
        __hasPendingRegeneratedChanges: true
      };
    });

    return combinedDate;
  }, []);

  const today = useMemo(() => {
    const now = new Date();
    now.setHours(0, 0, 0, 0);
    return now;
  }, []);

  useEffect(() => {
    const sourceDate = editedPost?.scheduled_time || editedPost?.scheduledDate;
    if (!sourceDate) {
      return;
    }

    const parsed = new Date(sourceDate);
    if (isNaN(parsed.getTime())) {
      return;
    }

    setSelectedDate(parsed);
    setPickerMonth(parsed);
    setSelectedTime(formatTimeForInput(sourceDate));
  }, [editedPost?.scheduledDate, editedPost?.scheduled_time, formatTimeForInput]);

  const handleDateSelect = useCallback((newDate) => {
    if (!newDate) return;
    const normalized = new Date(newDate);
    normalized.setHours(0, 0, 0, 0);
    if (normalized < today) {
      return;
    }
    setSelectedDate(newDate);
    setPickerMonth(newDate);
    const timeToUse = selectedTime || formatTimeForInput(editedPost?.scheduled_time || editedPost?.scheduledDate || null);
    const resolvedTime = timeToUse || '12:00';
    setSelectedTime(resolvedTime);
    combineDateAndTime(newDate, resolvedTime);
  }, [selectedTime, formatTimeForInput, editedPost?.scheduled_time, editedPost?.scheduledDate, combineDateAndTime, today]);

  const hourOptions = useMemo(() => Array.from({ length: 24 }, (_, i) => i.toString().padStart(2, '0')), []);
  const minuteOptions = useMemo(() => Array.from({ length: 60 }, (_, i) => i.toString().padStart(2, '0')), []);

  const handleTimeInputChange = useCallback((timeValue) => {
    if (!timeValue) return;
    const finalTime = timeValue.slice(0, 5);
    setSelectedTime(finalTime);
    const dateToUse = selectedDate || new Date();
    combineDateAndTime(dateToUse, finalTime);
    setIsTimePickerOpen(false);
  }, [selectedDate, combineDateAndTime]);

  const handleHourSelect = useCallback((hour) => {
    const [, minutes = '00'] = (selectedTime || '12:00').split(':');
    handleTimeInputChange(`${hour}:${minutes}`);
  }, [handleTimeInputChange, selectedTime]);

  const handleMinuteSelect = useCallback((minute) => {
    const [hours = '12'] = (selectedTime || '12:00').split(':');
    handleTimeInputChange(`${hours}:${minute}`);
  }, [handleTimeInputChange, selectedTime]);

  const inputDateTimeValue = useMemo(() => {
    const dateTime = editedPost?.scheduled_time || editedPost?.scheduledDate;
    if (!dateTime) return '';
    const date = new Date(dateTime);
    if (isNaN(date.getTime())) return '';
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    return `${year}-${month}-${day}T${hours}:${minutes}`;
  }, [editedPost?.scheduledDate, editedPost?.scheduled_time]);

  const displayValue = useMemo(() => {
    if (!selectedDate) return t('postReview.postEdit.time.placeholder', { defaultValue: 'Scegli data e ora' });
    const datePart = selectedDate.toLocaleDateString('it-IT', { weekday: 'short', day: 'numeric', month: 'short' });
    return `${datePart} · ${selectedTime || '—:—'}`;
  }, [selectedDate, selectedTime, t]);

  const openTimePicker = useCallback(() => {
    const el = timeInputRef.current;
    if (el) {
      el.focus();
    }
    setIsTimePickerOpen(true);
  }, []);

  useEffect(() => {
    if (!isTimePickerOpen) return undefined;
    const handleClickOutside = (event) => {
      if (timePanelRef.current?.contains(event.target) || timeInputRef.current?.contains(event.target)) {
        return;
      }
      setIsTimePickerOpen(false);
    };
    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('touchstart', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, [isTimePickerOpen]);

  const goToPreviousMonth = useCallback(() => {
    setPickerMonth(prev => {
      const next = new Date(prev);
      next.setMonth(prev.getMonth() - 1);
      return next;
    });
  }, []);

  const goToNextMonth = useCallback(() => {
    setPickerMonth(prev => {
      const next = new Date(prev);
      next.setMonth(prev.getMonth() + 1);
      return next;
    });
  }, []);

  const weekdayLabels = useMemo(() => ['Lun', 'Mar', 'Mer', 'Gio', 'Ven', 'Sab', 'Dom'], []);

  const calendarDays = useMemo(() => {
    const start = new Date(pickerMonth.getFullYear(), pickerMonth.getMonth(), 1);
    const leadingEmpty = (start.getDay() + 6) % 7; // Monday as first day
    const daysInMonth = new Date(pickerMonth.getFullYear(), pickerMonth.getMonth() + 1, 0).getDate();
    const days = [];

    for (let i = 0; i < leadingEmpty; i++) {
      days.push(null);
    }

    for (let day = 1; day <= daysInMonth; day++) {
      days.push(new Date(pickerMonth.getFullYear(), pickerMonth.getMonth(), day));
    }

    while (days.length % 7 !== 0) {
      days.push(null);
    }

    return days;
  }, [pickerMonth]);

  useEffect(() => {
    if (!isDatePickerOpen) return undefined;

    const handleClickOutside = (event) => {
      if (
        datePickerRef.current?.contains(event.target) ||
        dateFieldRef.current?.contains(event.target)
      ) {
        return;
      }
      setIsDatePickerOpen(false);
    };

    document.addEventListener('mousedown', handleClickOutside);
    document.addEventListener('touchstart', handleClickOutside);

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('touchstart', handleClickOutside);
    };
  }, [isDatePickerOpen]);

  // Handle individual date component changes
  const handleDateComponentChange = useCallback((component, value) => {
    setEditedPost(prev => {
      // Get current date components
      const currentDateTime = prev.scheduled_time || prev.scheduledDate || new Date().toISOString();
      const currentComponents = getDateComponents(currentDateTime);
      const currentTime = formatTimeForInput(currentDateTime);
      
      // Update the specific component (no padding here, just raw value)
      const newComponents = {
        ...currentComponents,
        [component]: value
      };
      
      // Only proceed if we have a valid number
      const numValue = parseInt(value);
      if (isNaN(numValue)) {
        return prev;
      }
      
      // Basic range validation (but allow partial input)
      if (component === 'day' && (numValue < 1 || numValue > 31)) {
        return prev;
      }
      if (component === 'month' && (numValue < 1 || numValue > 12)) {
        return prev;
      }
      if (component === 'year' && value.length === 4 && (numValue < 2020 || numValue > 2030)) {
        return prev;
      }
      
      // Only create date if all components are complete and valid
      const day = parseInt(newComponents.day);
      const month = parseInt(newComponents.month);
      const year = parseInt(newComponents.year);
      
      // Check if we have complete values
      if (day >= 1 && day <= 31 && month >= 1 && month <= 12 && year >= 2020 && year <= 2030) {
        // Pad the values for date creation
        const paddedDay = day.toString().padStart(2, '0');
        const paddedMonth = month.toString().padStart(2, '0');
        
        // Create new date
        const newDateString = `${year}-${paddedMonth}-${paddedDay}`;
        const combinedDateTime = new Date(`${newDateString}T${currentTime}:00`);
        
        // Check if the date is valid (handles cases like Feb 30, etc.)
        if (!isNaN(combinedDateTime.getTime())) {
          return {
            ...prev,
            scheduled_time: combinedDateTime.toISOString(),
            scheduledDate: combinedDateTime.toISOString()
          };
        }
      }
      
      // If date is incomplete or invalid, just return the previous state
      // This allows partial input without breaking
      return prev;
    });
  }, [getDateComponents, formatTimeForInput]);

  // Handle time change
  const handleTimeChange = useCallback((newTime) => {
    if (!newTime) return;
    
    setEditedPost(prev => {
      // Get current date components
      const currentDateTime = prev.scheduled_time || prev.scheduledDate || new Date().toISOString();
      const currentComponents = getDateComponents(currentDateTime);
      
      // Create date string from components
      const currentDate = `${currentComponents.year}-${currentComponents.month}-${currentComponents.day}`;
      
      // Combine existing date with new time
      const combinedDateTime = new Date(`${currentDate}T${newTime}:00`);
      
      return {
        ...prev,
        scheduled_time: combinedDateTime.toISOString(),
        scheduledDate: combinedDateTime.toISOString()
      };
    });
  }, [getDateComponents]);

  // Save handlers
  const handleSave = useCallback(async () => {
    if (!editedPost || isSaving) return;

    setIsSaving(true);

    try {
      const originalData = editedPost?.originalData || post;

      if (!originalData || !originalData._id) {
        throw new Error('No post data available to update');
      }

      console.log('[PostEditPage] Starting save with post ID:', originalData._id);

      // Prepare the update data - only include fields that have changed
      const updateData = {};

      // Check if content changed
      const originalContent = originalData.content || originalData.caption || originalData.description || '';
      const newContent = editedPost.description || editedPost.content || '';
      const contentChanged = newContent !== originalContent;
      if (contentChanged) {
        updateData.content = newContent;
        console.log('[PostEditPage] Content changed, will update');
      }

      // Check if scheduled_time changed
      const originalScheduledTime = originalData.scheduled_time || originalData.scheduledDate;
      const newScheduledTime = editedPost?.scheduled_time || editedPost?.scheduledDate;

      const scheduleChanged = Boolean(newScheduledTime) && newScheduledTime !== originalScheduledTime;

      if (scheduleChanged) {
        // Format the scheduled time properly
        const formatScheduledTime = (dateTime) => {
          if (!dateTime) return null;

          let date;
          if (dateTime instanceof Date) {
            date = dateTime;
          } else {
            date = new Date(dateTime);
          }

          // Get timezone offset in minutes
          const timezoneOffset = date.getTimezoneOffset();
          const offsetHours = Math.floor(Math.abs(timezoneOffset) / 60);
          const offsetMinutes = Math.abs(timezoneOffset) % 60;
          const offsetSign = timezoneOffset <= 0 ? '+' : '-';

          // Format: "2025-07-17T19:00:00+02:00" (preserves local timezone)
          const year = date.getFullYear();
          const month = String(date.getMonth() + 1).padStart(2, '0');
          const day = String(date.getDate()).padStart(2, '0');
          const hours = String(date.getHours()).padStart(2, '0');
          const minutes = String(date.getMinutes()).padStart(2, '0');
          const seconds = String(date.getSeconds()).padStart(2, '0');

          return `${year}-${month}-${day}T${hours}:${minutes}:${seconds}${offsetSign}${String(offsetHours).padStart(2, '0')}:${String(offsetMinutes).padStart(2, '0')}`;
        };

        updateData.scheduled_time = formatScheduledTime(newScheduledTime);
        console.log('[PostEditPage] Scheduled time changed, will update');
      }

      // Handle media array changes - use efficient update system
      let mediaUpdates = null;

      // Check if media changed (compare arrays)
      const originalMedia = originalData.media_urls || originalData.images || [];
      const newMedia = editedPost.images || [];
      const mediaChanged = JSON.stringify(originalMedia) !== JSON.stringify(newMedia);

      if (mediaChanged && editedPost.mediaArray && Array.isArray(editedPost.mediaArray)) {
        console.log('[PostEditPage] Media changed, preparing updates');
        // Prepare media updates for the new efficient system
        mediaUpdates = [];

        // Validate all media files first
        for (let i = 0; i < editedPost.mediaArray.length; i++) {
          const mediaItem = editedPost.mediaArray[i];

          if (mediaItem.type === 'file') {
            if (!(mediaItem.file instanceof File)) {
              throw new Error(`File ${i + 1}: Missing file reference`);
            }

            const file = mediaItem.file;
            const isVideo = file.type.startsWith('video/');
            const maxSize = isVideo ? 50 * 1024 * 1024 : 5 * 1024 * 1024; // 50MB for videos, 5MB for images
            const fileTypeLabel = isVideo ? 'Video' : 'Image';
            const sizeLabel = isVideo ? '50MB' : '5MB';

            if (file.size > maxSize) {
              throw new Error(`${fileTypeLabel} ${i + 1}: File size must be less than ${sizeLabel}`);
            }

            // Validate media format
            if (!SUPPORTED_MEDIA_MIME_TYPES.includes(file.type)) {
              throw new Error(`File ${i + 1}: Format must be ${SUPPORTED_MEDIA_LABEL}`);
            }

            mediaUpdates.push({
              type: 'file',
              file
            });
          } else if (mediaItem.type === 'url') {
            if (!mediaItem.url) {
              throw new Error(`Media ${i + 1}: Missing URL reference`);
            }

            mediaUpdates.push({
              type: 'url',
              url: mediaItem.url
            });
          }
        }

        // Validate total media count (max 10 items)
        if (mediaUpdates.length > 10) {
          throw new Error('Maximum 10 media items allowed per post');
        }
      }

      const hasCoreUpdates = (Object.keys(updateData).length > 0) || Boolean(mediaUpdates);

      // Ensure we have something to update
      if (!hasCoreUpdates) {
        console.log('[PostEditPage] No changes detected, skipping update');
        notify({
          variant: 'info',
          title: t('postReview.postEdit.alerts.noChanges', { defaultValue: 'Nessuna modifica da salvare.' })
        });
        return;
      }

      console.log('[PostEditPage] Calling postService.updatePost with:', {
        postId: originalData._id,
        updateData,
        hasMediaUpdates: !!mediaUpdates,
        scheduledTimeDetails: updateData.scheduled_time ? {
          raw: newScheduledTime,
          formatted: updateData.scheduled_time,
          parsedBack: new Date(updateData.scheduled_time)
        } : null
      });

      // Import postService directly
      const postService = (await import('../../../services/PostService')).default;

      // Call the PUT endpoint with the new efficient media system
      const response = await postService.updatePost(originalData._id, updateData, mediaUpdates);

      console.log('[PostEditPage] Update successful:', response);
      console.log('[PostEditPage] Updated post scheduled_time from response:', response.post?.scheduled_time);

      // If the backend changed the status (e.g., auto-approved on schedule change), restore the original one
      const originalStatus = extractStatusValue(originalData?.status);
      const responseStatus = extractStatusValue(response.post?.status);
      if (originalStatus && responseStatus && responseStatus !== originalStatus) {
        try {
          console.log('[PostEditPage] Restoring original status to prevent unintended approval:', {
            originalStatus,
            responseStatus
          });
          await postService.updatePostStatus(originalData._id, originalStatus);
          // Align local response with restored status
          if (response.post) {
            response.post.status = originalStatus;
          }
        } catch (statusError) {
          console.error('[PostEditPage] Failed to restore original status:', statusError);
        }
      }

      // Update the editedPost with the response to sync with backend
      if (response.post) {
        setEditedPost(prev => {
          if (prev?.mediaArray) {
            prev.mediaArray.forEach(item => revokePreviewUrl(item));
          }

          const updatedMediaUrls = response.post.media_urls || response.post.images || [];
          const updatedMediaArray = buildMediaArrayFromUrls(updatedMediaUrls);

          return {
            ...prev,
            ...response.post,
            // Ensure we have the right format for mobile
            images: buildImagesFromMediaArray(updatedMediaArray),
            media_urls: updatedMediaUrls,
            mediaArray: updatedMediaArray,
            description: response.post.content || response.post.description || prev.description,
            content: response.post.content || response.post.description || prev.description,
            __pendingRegeneratedImages: null,
            __pendingRegeneratedImageIndex: null,
            __pendingRegeneratedResponse: null,
            __pendingRegeneratedPrompt: null,
            __hasPendingRegeneratedChanges: false,
            // Update originalData for future change detection
            originalData: { ...prev.originalData, ...response.post }
          };
        });
      }

      // Trigger a refresh of the parent component if callback provided
      if (onSave) {
        await onSave(response.post || editedPost);
      }

      // Dispatch event to refresh MobilePostReview list
      window.dispatchEvent(new CustomEvent('postUpdated', {
        detail: { postId: originalData._id, updatedPost: response.post }
      }));

      // Show success message
      const updatedFields = [];
      if (updateData.content) updatedFields.push('content');
      if (updateData.scheduled_time) updatedFields.push('schedule');
      if (mediaUpdates) updatedFields.push('media');

      notify({
        variant: 'success',
        title: t('postReview.postEdit.alerts.saveSuccess', { defaultValue: 'Modifiche salvate con successo!' }),
        description: updatedFields.length
          ? t('postReview.postEdit.alerts.saveDetails', {
              defaultValue: `Campi aggiornati: ${updatedFields.join(', ')}`,
              fields: updatedFields.join(', ')
            })
          : undefined
      });

    } catch (error) {
      console.error('[PostEditPage] Error saving post:', error);
      notify({
        variant: 'error',
        title: t('postReview.postEdit.alerts.saveErrorTitle', { defaultValue: 'Errore durante il salvataggio' }),
        description: error.message || t('postReview.postEdit.alerts.saveErrorFallback', { defaultValue: 'Riprova.' })
      });
      if (apiClearError) apiClearError();
    } finally {
      setIsSaving(false);
    }
  }, [apiClearError, editedPost, isSaving, notify, onSave, post, revokePreviewUrl, t]);

  const handleUploadButtonClick = useCallback(() => {
    fileInputRef.current?.click();
  }, [fileInputRef]);

  const handleFileInputChange = useCallback((event) => {
    const files = event.target.files;
    if (!files || files.length === 0) {
      return;
    }

    const mediaItems = [];
    let hasInvalidFile = false;

    Array.from(files).forEach((file) => {
      if (typeof File !== 'undefined' && !(file instanceof File)) {
        hasInvalidFile = true;
        return;
      }

      if (!isSupportedMediaFile(file)) {
        hasInvalidFile = true;
        console.warn('[PostEditPage] Unsupported media format selected', {
          name: file?.name,
          type: file?.type
        });
        return;
      }

      const mediaItem = createMediaItemFromFile(file);
      if (mediaItem) {
        mediaItems.push(mediaItem);
      }
    });

    if (mediaItems.length > 0) {
      addMediaItemsToPost(mediaItems);
      console.log('[PostEditPage] Files added from device:', mediaItems.length);
    }

    if (hasInvalidFile) {
      notify({
        variant: 'error',
        title: unsupportedFormatMessage
      });
    }

    event.target.value = '';
  }, [addMediaItemsToPost, createMediaItemFromFile, notify, unsupportedFormatMessage]);

  const handleGenerateButtonClick = useCallback(() => {
    setAddImageModalMethod('ai');
    setShowAddImageModal(true);
  }, []);

  // Add image modal handlers
  const handleAddImageClick = useCallback(() => {
    setShowImageGallery(true);
  }, []);

  const handleAddImageClose = useCallback(() => {
    setShowAddImageModal(false);
    setAddImageModalMethod(null);
  }, []);

  const handleImageGalleryClose = useCallback(() => {
    setShowImageGallery(false);
  }, []);

  const handleImageGallerySelect = useCallback((image) => {
    const mediaItems = [];

    const pushFromSource = (item) => {
      if (!item) {
        return;
      }

      if (item.file instanceof File) {
        mediaItems.push(createMediaItemFromFile(item.file));
        return;
      }

      const sourceUrl = item.selectedUrl || item.url || item.displayUrl || item.raw?.public_url || item.raw?.url || item.raw?.file_url;
      const preview = item.displayUrl || item.previewUrl || sourceUrl;
      const mediaItem = createMediaItemFromUrl(sourceUrl, preview);
      if (mediaItem) {
        mediaItems.push(mediaItem);
      }
    };

    if (Array.isArray(image)) {
      image.forEach(pushFromSource);
    } else if (image?.files && Array.isArray(image.files)) {
      image.files.forEach((file) => {
        if (file instanceof File) {
          mediaItems.push(createMediaItemFromFile(file));
        }
      });
    } else {
      pushFromSource(image);
    }

    const sanitizedItems = mediaItems.filter(Boolean);

    if (sanitizedItems.length > 0) {
      addMediaItemsToPost(sanitizedItems);
      console.log('[PostEditPage] Media selected from gallery:', sanitizedItems.length);
    }

    setShowImageGallery(false);
  }, [addMediaItemsToPost, createMediaItemFromFile, createMediaItemFromUrl]);

  const handleImageGalleryUpload = useCallback((files) => {
    if (!files) {
      setShowImageGallery(false);
      return;
    }

    const fileArray = Array.isArray(files) ? files : Array.from(files);
    const mediaItems = fileArray
      .map(file => (file instanceof File ? createMediaItemFromFile(file) : null))
      .filter(Boolean);

    if (mediaItems.length > 0) {
      addMediaItemsToPost(mediaItems);
      console.log('[PostEditPage] Files added from gallery upload:', mediaItems.length);
    }

    setShowImageGallery(false);
  }, [addMediaItemsToPost, createMediaItemFromFile]);

  const handleGenerateImageFromModal = useCallback(async (source, options = {}) => {
    if (source !== 'ai') {
      if (typeof onAddImage === 'function') {
        return onAddImage(source, options);
      }
      return null;
    }

    const prompt = typeof options.prompt === 'string' ? options.prompt.trim() : '';
    if (!prompt) {
      throw new Error('Inserisci un prompt per generare l\'immagine');
    }

    if (prompt.length < 10) {
      throw new Error('Il prompt deve contenere almeno 10 caratteri');
    }

    if (prompt.length > 1000) {
      throw new Error('Il prompt può contenere al massimo 1000 caratteri');
    }

    const postId = getPostIdentity(editedPost);
    if (!postId) {
      throw new Error('Impossibile determinare il post da aggiornare');
    }

    try {
      const response = await postService.regenerateImage(postId, {
        user_prompt: prompt,
        count: options.count || 1,
        ratio: options.ratio || '4:5'
      });

      const generatedImages = extractRegeneratedImageUrls(response);
      const finalImageUrl = generatedImages[0];

      if (!finalImageUrl) {
        throw new Error('Il servizio non ha restituito immagini da utilizzare');
      }

      if (typeof onAddImage === 'function') {
        try {
          await onAddImage(source, {
            ...options,
            prompt,
            generatedImages,
            response,
            postId
          });
        } catch (callbackError) {
          console.warn('[PostEditPage] onAddImage callback failed after AI generation', callbackError);
        }
      }

      return {
        imageUrl: finalImageUrl,
        generatedImages,
        response
      };
    } catch (error) {
      console.error('[PostEditPage] Error during AI image generation from modal:', error);

      if (error?.errorData?.errors) {
        const { errors } = error.errorData;
        const promptErrors = errors.user_prompt || errors.prompt;
        const firstMessage = Array.isArray(promptErrors) ? promptErrors[0] : null;
        if (firstMessage) {
          throw new Error(firstMessage);
        }
      }

      if (error instanceof Error) {
        throw error;
      }

      throw new Error('Errore durante la generazione dell\'immagine');
    }
  }, [editedPost, extractRegeneratedImageUrls, getPostIdentity, onAddImage]);

  const handleAddImageConfirm = useCallback(async (newImageData) => {
    if (newImageData) {
      try {
        let mediaItem = null;

        if (newImageData instanceof File) {
          mediaItem = createMediaItemFromFile(newImageData);
        } else if (typeof newImageData === 'object' && newImageData.file instanceof File) {
          mediaItem = createMediaItemFromFile(newImageData.file);
        } else if (typeof newImageData === 'string') {
          mediaItem = createMediaItemFromUrl(newImageData);
        } else if (typeof newImageData === 'object' && typeof newImageData.url === 'string') {
          mediaItem = createMediaItemFromUrl(newImageData.url, newImageData.previewUrl);
        }

        if (mediaItem) {
          addMediaItemsToPost([mediaItem]);
        }

        // Note: The actual image upload to the backend happens when "Salva Modifiche" is clicked
        console.log('[PostEditPage] Image added to local state, will be uploaded on save');
      } catch (error) {
        console.error('[PostEditPage] Error adding image:', error);
        if (apiClearError) apiClearError();
      }
    }
    setShowAddImageModal(false);
    setAddImageModalMethod(null);
  }, [addMediaItemsToPost, createMediaItemFromUrl, apiClearError]);

  // AI Modal handlers
  const handleOpenAIModal = useCallback(() => {
    setAiModalType('text');
    setAiImageIndex(null);
    setShowAIModal(true);
  }, []);

  const handleCloseAIModal = useCallback(() => {
    setShowAIModal(false);
    setAiImageIndex(null);
  }, []);

  // Handle AI regeneration using preview-first API responses
  const handleAIRegeneration = useCallback(async (regenerationData) => {
    const { type, prompt, imageIndex = null, count, ratio } = regenerationData;
    const trimmedPrompt = (prompt || '').trim();

    console.log('[PostEditPage] Starting AI regeneration (preview aware):', {
      type,
      prompt: trimmedPrompt,
      postId: editedPost?.originalData?._id || editedPost?.id,
      imageIndex
    });

    setShowAIModal(false);

    const shouldShowContentSpinner = type === 'text' || type === 'description' || type === 'full' || type === 'complete';
    if (shouldShowContentSpinner) {
      console.log('[PostEditPage] Enabling AI loading animation');
      setIsAIGeneratingDescription(true);
    }

    try {
      const postId = editedPost?.originalData?._id || editedPost?.id;
      if (!postId) {
        throw new Error('No post ID available for AI regeneration');
      }

      if (type === 'text' || type === 'description') {
        console.log('[PostEditPage] Calling postService.regenerateContent (preview mode)');
        const response = await postService.regenerateContent(postId, {
          user_prompt: trimmedPrompt || undefined,
          preserve_hashtags: true,
          preserve_mentions: true
        });

        console.log('[PostEditPage] RegenerateContent response:', response);

        const regeneratedContent = response?.content || response?.regenerated_content?.content;
        if (!regeneratedContent) {
          throw new Error('La rigenerazione non ha restituito nuovo contenuto');
        }

        setEditedPost(prev => {
          if (!prev) {
            return prev;
          }

          return {
            ...prev,
            description: regeneratedContent,
            content: regeneratedContent,
            __hasPendingRegeneratedChanges: true
          };
        });

        notify({
          variant: 'success',
          title: t('postReview.postEdit.alerts.contentRegenerated', { defaultValue: 'Contenuto rigenerato!' }),
          description: t('postReview.postEdit.alerts.rememberToSave', { defaultValue: 'Ricordati di salvare per applicarlo.' })
        });

      } else if (type === 'image') {
        console.log('[PostEditPage] Calling postService.regenerateImage (preview mode)');
        const response = await postService.regenerateImage(postId, {
          user_prompt: trimmedPrompt || undefined,
          count: count || 1,
          ratio: ratio || '4:5'
        });

        console.log('[PostEditPage] RegenerateImage response:', response);

        const normalizedImages = extractRegeneratedImageUrls(response);

        if (!normalizedImages.length) {
          throw new Error('La rigenerazione non ha restituito immagini');
        }

        const mediaItems = normalizedImages
          .map((url) => createMediaItemFromUrl(url))
          .filter(Boolean);

        if (!mediaItems.length) {
          throw new Error('Impossibile utilizzare le immagini rigenerate restituite dal servizio');
        }

        setEditedPost(prev => {
          if (!prev) {
            return prev;
          }

          const baseArray = Array.isArray(prev.mediaArray) ? [...prev.mediaArray] : [];
          const safeIndex = typeof imageIndex === 'number' && imageIndex >= 0 ? imageIndex : baseArray.length;
          const [primaryItem, ...additionalItems] = mediaItems;

          if (safeIndex < baseArray.length) {
            const currentItem = baseArray[safeIndex];
            if (currentItem) {
              revokePreviewUrl(currentItem);
            }
            baseArray[safeIndex] = primaryItem;
          } else {
            baseArray.push(primaryItem);
          }

          if (additionalItems.length) {
            baseArray.push(...additionalItems);
          }

          const newImages = buildImagesFromMediaArray(baseArray);
          const newMediaUrls = buildMediaUrlsFromMediaArray(baseArray);

          return {
            ...prev,
            images: newImages,
            media_urls: newMediaUrls,
            mediaArray: baseArray,
            __pendingRegeneratedImages: null,
            __pendingRegeneratedImageIndex: null,
            __pendingRegeneratedResponse: null,
            __pendingRegeneratedPrompt: null,
            __hasPendingRegeneratedChanges: true
          };
        });

        notify({
          variant: 'success',
          title: t('postReview.postEdit.alerts.imageRegenerated', { defaultValue: 'Immagine rigenerata!' }),
          description: t('postReview.postEdit.alerts.rememberToSave', { defaultValue: 'Ricordati di salvare per confermare.' })
        });

      } else if (type === 'full' || type === 'complete') {
        console.log('[PostEditPage] Calling postService.regeneratePost');
        const response = await postService.regeneratePost(postId, {
          user_prompt: trimmedPrompt || undefined,
          image_count: count || 1,
          image_ratio: ratio || '4:5'
        });

        console.log('[PostEditPage] RegeneratePost response:', response);

        if (!response?.post) {
          throw new Error('La rigenerazione completa non ha restituito un post aggiornato');
        }

        setEditedPost(prev => {
          if (!prev) {
            return prev;
          }

          const hasNewMedia = Array.isArray(response.post.media_urls) || Array.isArray(response.post.images);
          let updatedMediaArray = prev.mediaArray || [];
          let updatedImages = prev.images;
          let updatedMediaUrls = prev.media_urls;

          if (hasNewMedia) {
            const sourceMediaUrls = response.post.media_urls || response.post.images || [];
            if (prev.mediaArray) {
              prev.mediaArray.forEach(item => revokePreviewUrl(item));
            }
            updatedMediaArray = buildMediaArrayFromUrls(sourceMediaUrls);
            updatedImages = buildImagesFromMediaArray(updatedMediaArray);
            updatedMediaUrls = sourceMediaUrls;
          }

          const newContent = response.post.content || prev.description;

          return {
            ...prev,
            ...response.post,
            description: newContent,
            content: newContent,
            images: updatedImages,
            media_urls: updatedMediaUrls,
            mediaArray: updatedMediaArray,
            __pendingRegeneratedImages: null,
            __pendingRegeneratedImageIndex: null,
            __pendingRegeneratedResponse: null,
            __pendingRegeneratedPrompt: null,
            __hasPendingRegeneratedChanges: false,
            originalData: { ...prev.originalData, ...response.post }
          };
        });

        notify({
          variant: 'success',
          title: t('postReview.postEdit.alerts.postRegenerated', { defaultValue: 'Post rigenerato con successo!' })
        });
      } else {
        console.warn('[PostEditPage] Unsupported AI regeneration type:', type);
      }

      console.log('[PostEditPage] AI regeneration completed successfully');

    } catch (error) {
      console.error('[PostEditPage] Error with AI regeneration:', error);
      notify({
        variant: 'error',
        title: t('postReview.postEdit.alerts.aiErrorTitle', { defaultValue: 'Errore durante la rigenerazione AI' }),
        description: error.message || t('postReview.postEdit.alerts.aiErrorFallback', { defaultValue: 'Riprova.' })
      });
      if (apiClearError) apiClearError();
    } finally {
      if (shouldShowContentSpinner) {
        setIsAIGeneratingDescription(false);
      }
    }
  }, [apiClearError, editedPost, extractRegeneratedImageUrls, notify, revokePreviewUrl, t]);

  // Early return with loading or error state based on post availability
  if (!post) {
    // If post is null, show loading state (normal during navigation)
    console.log('[PostEditPage] Post is null, showing loading state');
    return renderSkeletonPage();
  }
  
  // If post exists but is invalid, show error
  if (!validatePostData(post)) {
    console.error('[PostEditPage] Invalid post data provided:', post);
    const invalidPostId = post.id || post._id || t('postReview.postEdit.error.missingId');
    return (
      <div className="post-edit-page">
        <div className="post-edit-error">
          <h2>{t('postReview.postEdit.error.title')}</h2>
          <p>{t('postReview.postEdit.error.invalid')}</p>
          <p style={{ fontSize: '0.9rem', opacity: 0.7, marginTop: '0.5rem' }}>
            {t('postReview.postEdit.error.id', { id: invalidPostId })}
          </p>
          <button 
            className="btn-base btn-lg btn-secondary"
            onClick={handleCloseAttempt}
          >
            {t('postReview.postEdit.error.back')}
          </button>
        </div>
      </div>
    );
  }

  console.log('[PostEditPage] Rendering with editedPost:', editedPost);

  // Skeleton when loading or not initialized
  if (propIsLoading || !editedPost) {
    return renderSkeletonPage();
  }

  const monthLabel = pickerMonth.toLocaleDateString('it-IT', { month: 'long', year: 'numeric' });

  return (
    <>
      <div className="post-edit-page">
      {/* Content */}
      <div className="post-edit-content">
        {/* Header */}
        <div className="post-edit-header">
          <button className="post-edit-back-button" onClick={handleCloseAttempt}>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
              <path d="M15 18l-6-6 6-6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
            </svg>
          </button>
          <h3>
            {editedPost?.platform === 'instagram' ? 'Modifica Instagram' :
             editedPost?.platform === 'facebook' ? 'Modifica Facebook' :
             editedPost?.platform === 'linkedin' ? 'Modifica LinkedIn' :
             editedPost?.platform === 'x' ? 'Modifica X' :
             'Modifica Post'}
          </h3>
          <div className="post-edit-header-spacer"></div>
        </div>
            {/* Images Section */}
            <div className="post-edit-section">
              <h2 className="post-edit-section-title">{t('postReview.postEdit.images.title')}</h2>
              <p className="post-edit-section-description">
                {t('postReview.postEdit.images.helper')}
              </p>
              <div className="post-edit-images-grid" key={`grid-${forceRenderKey}`}>
                {editedPost.images?.map((image, index) => {
                  const mediaItem = editedPost.mediaArray?.[index];
                  const sourceUrl = mediaItem?.url || image;
                  const isVideo = mediaItem?.type === 'file'
                    ? !!mediaItem.file && mediaItem.file.type.startsWith('video/')
                    : isVideoFile(sourceUrl);
                  const thumbnailKey = `${mediaItem?.id || image}-${index}`;
                  const isLoading = thumbnailsLoading[thumbnailKey];
                  const thumbnail = videoThumbnails[thumbnailKey];
                  const blurScopeBase = `${imageLoadingScope}-media-${index}`;
                  const imageLoadingProps = !isVideo
                    ? getImageLoadingProps(sourceUrl || image, blurScopeBase)
                    : null;
                  const videoThumbnailLoadingProps = isVideo && thumbnail
                    ? getImageLoadingProps(thumbnail, `${blurScopeBase}-video`)
                    : null;

                  const renderMediaContent = () => {
                    if (isVideo && isLoading) {
                      return (
                        <div className="post-edit-image-loading">
                          <div className="post-edit-thumbnail-spinner"></div>
                          <span className="post-edit-loading-text">{t('postReview.postEdit.media.generatingThumbnail')}</span>
                        </div>
                      );
                    }

                    if (isVideo && thumbnail) {
                      return (
                        <>
                          <img
                            src={thumbnail}
                            alt={`Video thumbnail ${index + 1}`}
                            className={getImageClassNames('post-edit-image', videoThumbnailLoadingProps)}
                            loading="lazy"
                            decoding="async"
                            onError={(e) => {
                              videoThumbnailLoadingProps?.onError?.(e);
                              e.target.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAwIiBoZWlnaHQ9IjI1MCIgdmlld0JveD0iMCAwIDIwMCAyNTAiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+CjxyZWN0IHdpZHRoPSIyMDAiIGhlaWdodD0iMjUwIiBmaWxsPSJyZ2JhKDI1NSwgMjU1LCAyNTUsIDAuMSkiLz4KPHJlY3QgeD0iMjAiIHk9IjgwIiB3aWR0aD0iMTYwIiBoZWlnaHQ9IjkwIiByeD0iNSIgZmlsbD0ibm9uZSIgc3Ryb2tlPSJyZ2JhKDI1NSwgMjU1LCAyNTUsIDAuMykiIHN0cm9rZS13aWR0aD0iMSIvPgo8Y2lyY2xlIGN4PSI2MCIgY3k9IjEwMCIgcj0iOCIgZmlsbD0icmdiYSgyNTUsIDI1NSwgMjU1LCAwLjMpIi8+CjxwYXRoIGQ9Im0xNjAgMTQwLTQwLTQwLTI1IDI1LTE1LTE1IiBzdHJva2U9InJnYmEoMjU1LCAyNTUsIDI1NSwgMC4zKSIgc3Rva2Utd2lkdGg9IjIiIHN0cm9rZS1saW5lY2FwPSJyb3VuZCIgc3Ryb2tlLWxpbmVqb2luPSJyb3VuZCIvPgo8L3N2Zz4=';
                            }}
                            onLoad={(event) => {
                              videoThumbnailLoadingProps?.onLoad?.(event);
                            }}
                          />
                          <div className="post-edit-video-indicator">
                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                              <polygon points="5,3 19,12 5,21" fill="white"/>
                            </svg>
                          </div>
                        </>
                      );
                    }

                    if (isVideo) {
                      return (
                        <div className="post-edit-image-loading">
                          <div className="post-edit-video-placeholder">🎥</div>
                          <span className="post-edit-loading-text">{t('postReview.postEdit.media.videoLabel')}</span>
                        </div>
                      );
                    }

                    return (
                      <img
                        src={image}
                        alt={t('postReview.postEdit.media.imageAlt', { index: index + 1 })}
                        className={getImageClassNames('post-edit-image', imageLoadingProps)}
                        loading="lazy"
                        decoding="async"
                        onError={(e) => {
                          imageLoadingProps?.onError?.(e);
                          e.target.src = 'data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iMjAwIiBoZWlnaHQ9IjI1MCIgdmlld0JveD0iMCAwIDIwMCAyNTAiIGZpbGw9Im5vbmUiIHhtbG5zPSJodHRwOi8vd3d3LnczLm9yZy8yMDAwL3N2ZyI+CjxyZWN0IHdpZHRoPSIyMDAiIGhlaWdodD0iMjUwIiBmaWxsPSJyZ2JhKDI1NSwgMjU1LCAyNTUsIDAuMSkiLz4KPHJlY3QgeD0iMjAiIHk9IjgwIiB3aWR0aD0iMTYwIiBoZWlnaHQ9IjkwIiByeD0iNSIgZmlsbD0ibm9uZSIgc3Ryb2tlPSJyZ2JhKDI1NSwgMjU1LCAyNTUsIDAuMykiIHN0cm9rZS13aWR0aD0iMSIvPgo8Y2lyY2xlIGN4PSI2MCIgY3k9IjEwMCIgcj0iOCIgZmlsbD0icmdiYSgyNTUsIDI1NSwgMjU1LCAwLjMpIi8+CjxwYXRoIGQ9Im0xNjAgMTQwLTQwLTQwLTI1IDI1LTE1LTE1IiBzdHJva2U9InJnYmEoMjU1LCAyNTUsIDI1NSwgMC4zKSIgc3Rva2Utd2lkdGg9IjIiIHN0cm9rZS1saW5lY2FwPSJyb3VuZCIgc3Ryb2tlLWxpbmVqb2luPSJyb3VuZCIvPgo8L3N2Zz4=';
                        }}
                        onLoad={(event) => {
                          imageLoadingProps?.onLoad?.(event);
                        }}
                      />
                    );
                  };

                  return (
                    <div
                      key={`${editedPost.mediaArray?.[index]?.id || index}-${forceRenderKey}`}
                      className={`post-edit-image-item ${draggedIndex === index && isDragging ? 'dragging' : ''} ${dragOverIndex === index && isDragging ? 'drag-target' : ''} ${pressedImageIndex === index && !isDragging ? 'pressed' : ''}`}
                      data-index={index}
                    >
                      {/* Image */}
                      <div 
                        className={`post-edit-image-container ${draggedIndex === index && isDragging ? 'being-dragged' : ''}`}
                        data-image-index={index}
                        onMouseDown={(e) => handleImageMouseDown(e, index)}
                        onTouchStart={(e) => handleImageTouchStart(e, index)}
                      >
                        {renderMediaContent()}

                        {!isDragging && !isVideo && (
                          <div className="post-edit-image-ai-overlay">
                            <button
                              className="post-edit-image-ai-btn"
                              onClick={() => handleAIRegenerateImage(index)}
                              aria-label={t('postReview.postEdit.media.regenerateAria')}
                            >
                              <AIIcon size={14} />
                            </button>
                          </div>
                        )}

                        {/* Image Number - Bottom Left */}
                        <div className="post-edit-image-number">
                          {index + 1}
                        </div>
                      </div>
                    </div>
                  );
                })}
                
                {/* Add Image Button / Delete Zone */}
                <div className={`post-edit-add-image-item ${isDragging ? 'delete-zone' : ''} ${isDragging && isOverTrash ? 'active' : ''}`}>
                  {!isDragging ? (
                    <div className="post-edit-add-image-content">
                      <div className="post-edit-add-image-buttons">
                        <button
                          type="button"
                          className="post-edit-add-image-main-btn"
                          onClick={handleUploadButtonClick}
                          aria-label={t('postReview.postEdit.media.uploadAria')}
                        >
                          <span className="post-edit-add-image-btn-icon">
                            <UploadIcon size={18} />
                          </span>
                          <span>{t('postReview.postEdit.media.uploadLabel')}</span>
                        </button>

                        <button
                          type="button"
                          className="post-edit-add-image-main-btn"
                          onClick={handleGenerateButtonClick}
                          aria-label={t('postReview.postEdit.media.generateAria')}
                        >
                          <span className="post-edit-add-image-btn-icon">
                            <img src={iconAi} alt="AI" />
                          </span>
                          <span>{t('postReview.postEdit.media.generateLabel')}</span>
                        </button>

                        <button
                          type="button"
                          className="post-edit-add-image-main-btn"
                          onClick={handleAddImageClick}
                          aria-label={t('postReview.postEdit.media.galleryAria')}
                        >
                          <span className="post-edit-add-image-btn-icon">
                            <GalleryIcon size={18} />
                          </span>
                          <span>{t('postReview.postEdit.media.galleryLabel')}</span>
                        </button>
                      </div>

                      <input
                        ref={fileInputRef}
                        type="file"
                        accept={SUPPORTED_MEDIA_ACCEPT}
                        multiple
                        onChange={handleFileInputChange}
                        style={{ display: 'none' }}
                      />
                    </div>
                  ) : (
                    <div className="post-edit-delete-zone" data-delete-zone="true">
                      <div className="post-edit-delete-icon">
                        <TrashIcon size={32} />
                      </div>
                      <span className="post-edit-delete-text">
                        {isOverTrash
                          ? t('postReview.postEdit.media.deleteDrop')
                          : t('postReview.postEdit.media.deleteDrag')}
                      </span>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Date and Time Section */}
            <div className="post-edit-section">
              <div className="post-edit-section-header">
                <div className="post-edit-section-title-group">
                  <h2 className="post-edit-section-title">{t('postReview.postEdit.time.title')}</h2>
                </div>
              </div>
              <p className="post-edit-section-description">
                {t('postReview.postEdit.time.subtitle')}
              </p>
              
              <div className="post-edit-datetime-container mobile-datetime-container">
                <div className="mobile-datetime-field date-picker-field" ref={dateFieldRef}>
                  <label htmlFor="mobile-datetime" className="mobile-datetime-label">
                    <CalendarIcon size={18} />
                    <span>{t('postReview.postEdit.time.label')}</span>
                  </label>

                  <div className="date-picker-display-group">
                    <input
                      id="mobile-datetime"
                      type="text"
                      className="date-picker-input-display"
                      value={displayValue}
                      readOnly
                      onFocus={() => !isSaving && setIsDatePickerOpen(true)}
                      onClick={() => !isSaving && setIsDatePickerOpen(true)}
                      aria-expanded={isDatePickerOpen}
                      aria-controls="mobile-date-picker-popover"
                      disabled={isSaving}
                    />
                    <button
                      type="button"
                      className="date-picker-icon-btn"
                      onClick={() => !isSaving && setIsDatePickerOpen(prev => !prev)}
                      aria-label={t('postReview.postEdit.time.label')}
                      aria-expanded={isDatePickerOpen}
                      aria-controls="mobile-date-picker-popover"
                      disabled={isSaving}
                    >
                      <CalendarIcon size={16} />
                    </button>
                  </div>

                  {isDatePickerOpen && (
                    <div
                      className="mobile-date-picker-overlay"
                      onClick={() => setIsDatePickerOpen(false)}
                      aria-hidden="true"
                    >
                      <div
                        className="mobile-date-picker-popover"
                        id="mobile-date-picker-popover"
                        ref={datePickerRef}
                        role="dialog"
                        aria-label={t('postReview.postEdit.time.label')}
                        onClick={(e) => e.stopPropagation()}
                      >
                        <div className="mobile-date-picker comp-503">
                          <div className="mobile-date-picker-header">
                            <button
                              type="button"
                              className="date-picker-nav"
                              onClick={goToPreviousMonth}
                              aria-label={t('postReview.postEdit.time.prevMonth', { defaultValue: 'Mese precedente' })}
                            >
                              <BackIcon size={18} />
                            </button>
                            <div className="mobile-date-picker-month">{monthLabel}</div>
                            <button
                              type="button"
                              className="date-picker-nav next"
                              onClick={goToNextMonth}
                              aria-label={t('postReview.postEdit.time.nextMonth', { defaultValue: 'Mese successivo' })}
                            >
                              <BackIcon size={18} />
                            </button>
                          </div>

                          <div className="mobile-date-picker-body single-column">
                            <div className="mobile-date-picker-calendar">
                              <div className="mobile-date-picker-weekdays">
                                {weekdayLabels.map((label) => (
                                  <span key={label} className="mobile-date-picker-weekday">
                                    {label}
                                  </span>
                                ))}
                              </div>
                              <div className="mobile-date-picker-grid">
                                {calendarDays.map((day, index) => {
                                  if (!day) {
                                    return <span key={`empty-${index}`} className="mobile-date-picker-day empty"></span>;
                                  }
                                  const isSelected = isSameDay(day, selectedDate);
                                  const isToday = isSameDay(day, new Date());
                                  const isDisabled = day < today;
                                  return (
                                    <button
                                      type="button"
                                      key={day.getTime()}
                                      className={`mobile-date-picker-day ${isSelected ? 'selected' : ''} ${isToday ? 'today' : ''} ${isDisabled ? 'disabled' : ''}`}
                                      onClick={() => handleDateSelect(day)}
                                      disabled={isSaving || isDisabled}
                                    >
                                      <span>{day.getDate()}</span>
                                      {isToday && <span className="mobile-date-picker-dot"></span>}
                                    </button>
                                  );
                                })}
                              </div>
                            </div>

                            <div className="mobile-date-picker-time-input">
                              <div className="time-input-label-row">
                                <ClockIcon size={16} />
                                <span>{t('postReview.postEdit.time.chooseHour', { defaultValue: 'Scegli orario' })}</span>
                              </div>
                            <div className="time-input-wrapper" onClick={openTimePicker} role="button" tabIndex={0}>
                              <input
                                type="time"
                                step="60"
                                value={selectedTime || '12:00'}
                                onChange={(e) => handleTimeInputChange(e.target.value)}
                                className="time-input-field"
                                disabled={isSaving}
                                ref={timeInputRef}
                              />
                              <div className="time-input-icon">
                                <ClockIcon size={16} aria-hidden="true" />
                              </div>

                              {isTimePickerOpen && (
                                <div className="time-picker-panel" ref={timePanelRef}>
                                  <div className="time-picker-columns">
                                    <div className="time-picker-column">
                                      <div className="time-picker-column-label">{t('postReview.postEdit.time.hours', { defaultValue: 'Ore' })}</div>
                                      <div className="time-picker-scroll">
                                        {hourOptions.map((hour) => {
                                          const isSelected = (selectedTime || '12:00').startsWith(hour);
                                          return (
                                            <button
                                              type="button"
                                              key={hour}
                                              className={`time-picker-option ${isSelected ? 'selected' : ''}`}
                                              onClick={() => handleHourSelect(hour)}
                                            >
                                              {hour}
                                            </button>
                                          );
                                        })}
                                      </div>
                                    </div>
                                    <div className="time-picker-column">
                                      <div className="time-picker-column-label">{t('postReview.postEdit.time.minutes', { defaultValue: 'Minuti' })}</div>
                                      <div className="time-picker-scroll">
                                        {minuteOptions.map((minute) => {
                                          const isSelected = (selectedTime || '12:00').split(':')[1] === minute;
                                          return (
                                            <button
                                              type="button"
                                              key={minute}
                                              className={`time-picker-option ${isSelected ? 'selected' : ''}`}
                                              onClick={() => handleMinuteSelect(minute)}
                                            >
                                              {minute}
                                            </button>
                                          );
                                        })}
                                      </div>
                                    </div>
                                  </div>
                                </div>
                              )}
                            </div>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>

            {/* Text Section */}
            <div className="post-edit-section">
              <div className="post-edit-section-header">
                <div className="post-edit-section-title-group">
                  <h2 className="post-edit-section-title">{t('postReview.postEdit.description.title')}</h2>
                  <button
                    className="btn-base btn-sm btn-secondary"
                    onClick={handleOpenAIModal}
                    disabled={isSaving}
                    aria-label={t('postReview.postEdit.description.aiAria')}
                  >
                    <AIIcon size={16} />
                  </button>
                </div>
              </div>
              <p className="post-edit-section-description">
                {t('postReview.postEdit.description.subtitle')}
              </p>
              
              <div className="post-edit-text-container">
                {isAIGeneratingDescription ? (
                  <div className="post-edit-textarea-wrapper ai-generating">
                    <AILoadingAnimation text={t('postReview.postEdit.aiGenerating')} />
                  </div>
                ) : (
                  <textarea
                    className="post-edit-textarea"
                    value={editedPost.description || ''}
                    onChange={(e) => handleTextChange(e.target.value)}
                    placeholder={t('postReview.postEdit.description.placeholder')}
                    rows={8}
                    disabled={isSaving}
                  />
                )}
                
                {/* Save Button */}
                <div className="post-edit-text-save-section">
                  <button
                    className="btn-base btn-lg btn-gradient post-edit-text-save-btn"
                    onClick={handleSave}
                    disabled={isSaving}
                  >
                    {isSaving ? (
                      <div className="post-edit-loading-spinner" />
                    ) : (
                      <SaveIcon size={18} />
                    )}
                    <span>{isSaving ? t('postReview.postEdit.description.saving') : t('postReview.postEdit.description.save')}</span>
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Add Image Modal */}
          {showAddImageModal && (
          <AddImageModal
            isOpen={showAddImageModal}
            onClose={handleAddImageClose}
            onConfirm={handleAddImageConfirm}
            onAddImage={handleGenerateImageFromModal}
            initialMethod={addImageModalMethod}
            accept={SUPPORTED_MEDIA_ACCEPT}
          />
        )}

          {/* AI Regeneration Modal: continua per testo/full; immagini usano Prompt Editor */}
          {showAIModal && aiModalType !== 'image' && (
            <AIRegenerationModal
              isOpen={showAIModal}
              onClose={handleCloseAIModal}
              type={aiModalType}
              post={editedPost}
              imageIndex={aiImageIndex}
              onRegenerate={handleAIRegeneration}
            />
          )}

          {/* Drag Ghost Image */}
          {isDragging && draggedIndex !== null && (
            <div 
              className="post-edit-drag-ghost"
              style={{
                position: 'fixed',
                left: dragCurrentPos.x - (draggedImageDimensions.width / 2), // Centered based on real width
                top: dragCurrentPos.y - (draggedImageDimensions.height / 2),  // Centered based on real height
                width: `${draggedImageDimensions.width}px`,
                height: `${draggedImageDimensions.height}px`,
                pointerEvents: 'none',
                zIndex: 1000,
                opacity: 0.85,
                transform: 'rotate(2deg) scale(1.02)', // Slightly bigger for visual feedback
                borderRadius: '12px',
                overflow: 'hidden',
                border: '2px solid rgba(255, 255, 255, 0.8)',
                boxShadow: '0 10px 30px rgba(0, 0, 0, 0.45)',
                transition: 'none',
              }}
            >
              {(() => {
                if (!editedPost?.images || draggedIndex === null || !editedPost.images[draggedIndex]) {
                  return null;
                }
                
                const mediaItem = editedPost.mediaArray?.[draggedIndex];
                const draggedImageUrl = mediaItem?.previewUrl || mediaItem?.url || editedPost.images[draggedIndex];
                const thumbnailKey = `${mediaItem?.id || draggedImageUrl}-${draggedIndex}`;
                const isVideo = mediaItem?.type === 'file'
                  ? !!mediaItem.file && mediaItem.file.type.startsWith('video/')
                  : isVideoFile(mediaItem?.url || draggedImageUrl);
                const thumbnail = videoThumbnails[thumbnailKey];
                
                return (
                  <>
                    <img
                      src={isVideo && thumbnail ? thumbnail : draggedImageUrl}
                      alt={`Dragging ${isVideo ? 'video' : 'image'} ${draggedIndex + 1}`}
                      style={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover',
                      }}
                    />
                    {/* Video indicator for dragged video */}
                    {isVideo && thumbnail && (
                      <div style={{
                        position: 'absolute',
                        top: '8px',
                        right: '8px',
                        width: '24px',
                        height: '24px',
                        background: 'rgba(0, 0, 0, 0.7)',
                        borderRadius: '50%',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}>
                        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <polygon points="5,3 19,12 5,21" fill="white"/>
                        </svg>
                      </div>
                    )}
                  </>
                );
              })()}
              
              {/* Ghost image number */}
              <div 
                style={{
                  position: 'absolute',
                  bottom: '8px',
                  left: '8px',
                  width: '28px',
                  height: '28px',
                  background: 'rgba(255, 255, 255, 0.92)',
                  color: '#111111',
                  borderRadius: '50%',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  fontSize: '14px',
                  fontWeight: '700',
                  border: '1px solid rgba(0, 0, 0, 0.08)',
                  boxShadow: '0 2px 6px rgba(0, 0, 0, 0.25)',
                }}
              >
                {draggedIndex + 1}
              </div>
            </div>
          )}



          {/* Delete Confirmation Modal */}
          {showDeleteConfirm && (
            <DeleteConfirmModal
              isOpen={showDeleteConfirm}
              imageIndex={deleteImageIndex}
              onConfirm={handleConfirmDelete}
              onCancel={handleCancelDelete}
            />
          )}
      </div>
      {showImageGallery && (
        <div
          className="mobile-image-gallery-overlay"
          role="dialog"
          aria-modal="true"
          data-mobile-swipe-blocker="true"
        >
          <ImageGalleryPage
            onBack={handleImageGalleryClose}
            onImageSelect={handleImageGallerySelect}
            onUploadFiles={handleImageGalleryUpload}
            viewerOrigin="post-edit"
          />
        </div>
      )}
    </>
  );
};

// Add Image Modal Component
const AddImageModal = ({
  onClose,
  onConfirm,
  onAddImage,
  initialMethod = null,
  accept = SUPPORTED_MEDIA_ACCEPT
}) => {
  const { t } = useLanguage();
  const { notify } = useMobileAlert();
  const unsupportedFormatMessage = useMemo(
    () => t('postReview.postEdit.media.unsupportedFormat', { defaultValue: `Formato non supportato. Usa: ${SUPPORTED_MEDIA_LABEL}` }),
    [t]
  );
  const [selectedMethod, setSelectedMethod] = useState(initialMethod || null); // 'upload' or 'ai'
  const [aiPrompt, setAiPrompt] = useState('');
  const [isGenerating, setIsGenerating] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');
  const fileInputRef = useRef(null);

  useEffect(() => {
    setSelectedMethod(initialMethod || null);
    setAiPrompt('');
    setErrorMessage('');
  }, [initialMethod]);

  const handleUploadClick = useCallback(() => {
    setSelectedMethod('upload');
    setErrorMessage('');
    if (fileInputRef.current) {
      fileInputRef.current.click();
    }
  }, []);

  const handleFileChange = useCallback((e) => {
    const file = e.target.files?.[0];
    if (!file) {
      return;
    }

    if (!isSupportedMediaFile(file)) {
      notify({
        variant: 'error',
        title: unsupportedFormatMessage
      });
      setSelectedMethod(null);
      e.target.value = '';
      return;
    }

    setErrorMessage('');
    onConfirm({ file });
    e.target.value = '';
  }, [notify, onConfirm, unsupportedFormatMessage]);

  const handleAIClick = useCallback(() => {
    setSelectedMethod('ai');
    setErrorMessage('');
  }, []);

  const handleAIGenerate = useCallback(async () => {
    const trimmedPrompt = aiPrompt.trim();
    if (!trimmedPrompt) {
      setErrorMessage('Inserisci un prompt per generare l\'immagine');
      return;
    }
    if (trimmedPrompt.length < 10) {
      setErrorMessage('Il prompt deve contenere almeno 10 caratteri');
      return;
    }
    if (trimmedPrompt.length > 1000) {
      setErrorMessage('Il prompt può contenere al massimo 1000 caratteri');
      return;
    }
    
    setIsGenerating(true);
    setErrorMessage('');
    try {
      if (onAddImage) {
        const result = await onAddImage('ai', { prompt: trimmedPrompt });
        if (Array.isArray(result?.generatedImages) && result.generatedImages.length > 0) {
          onConfirm(result.generatedImages[0]);
        } else if (result?.imageUrl) {
          onConfirm(result.imageUrl);
        } else {
          throw new Error('Il servizio non ha restituito alcuna immagine');
        }
        setAiPrompt('');
      }
    } catch (error) {
      console.error('Error generating AI image:', error);
      setErrorMessage(error?.message || 'Errore durante la generazione dell\'immagine');
    } finally {
      setIsGenerating(false);
    }
  }, [aiPrompt, onAddImage, onConfirm]);

  const handleOverlayClick = useCallback((e) => {
    if (e.target === e.currentTarget) {
      onClose();
    }
  }, [onClose]);

  return (
    <div className="add-image-modal-overlay" onClick={handleOverlayClick}>
      <div className="add-image-modal">
        {/* Header */}
        <div className="add-image-modal-header">
          <h3 className="add-image-modal-title">{t('postReview.postEdit.addImage.title')}</h3>
          <button
            className="add-image-modal-close"
            onClick={onClose}
            aria-label={t('postReview.postEdit.addImage.aria.close')}
          >
            <CloseIcon size={20} />
          </button>
        </div>

        {/* Content */}
        <div className="add-image-modal-content">
          {!selectedMethod ? (
            // Method Selection
            <div className="add-image-methods">
              <button
                className="add-image-method-btn upload"
                onClick={handleUploadClick}
              >
                <div className="add-image-method-icon">📷</div>
                <h4>{t('postReview.postEdit.addImage.methods.upload.title')}</h4>
                <p>{t('postReview.postEdit.addImage.methods.upload.description')}</p>
              </button>
              
              <button
                className="add-image-method-btn ai"
                onClick={handleAIClick}
              >
                <div className="add-image-method-icon">
                  <AIIcon size={24} />
                </div>
                <h4>{t('postReview.postEdit.addImage.methods.ai.title')}</h4>
                <p>{t('postReview.postEdit.addImage.methods.ai.description')}</p>
              </button>
            </div>
          ) : selectedMethod === 'ai' ? (
            // AI Generation
            <div className="add-image-ai-section">
              <div className="add-image-ai-input">
                <label htmlFor="ai-prompt">{t('postReview.postEdit.addImage.aiForm.label')}</label>
                <textarea
                  id="ai-prompt"
                  className="add-image-ai-textarea"
                  value={aiPrompt}
                  onChange={(e) => setAiPrompt(e.target.value)}
                  placeholder={t('postReview.postEdit.addImage.aiForm.placeholder')}
                  rows={4}
                  disabled={isGenerating}
                />
              </div>
              
              <div className="add-image-ai-actions">
                <button
                  className="add-image-btn secondary"
                  onClick={() => setSelectedMethod(null)}
                  disabled={isGenerating}
                >
                  {t('postReview.postEdit.addImage.aiForm.back')}
                </button>
                <button
                  className="add-image-btn primary"
                  onClick={handleAIGenerate}
                  disabled={!aiPrompt.trim() || isGenerating}
                >
                  {isGenerating ? (
                    <>
                      <div className="add-image-loading-spinner" />
                      {t('postReview.postEdit.addImage.aiForm.generating')}
                    </>
                  ) : (
                    <>
                      <AIIcon size={16} />
                      {t('postReview.postEdit.addImage.aiForm.submit')}
                    </>
                  )}
                </button>
              </div>
              {errorMessage && (
                <div
                  className="add-image-ai-error"
                  style={{ marginTop: 12, color: '#ff4d4f', fontSize: '0.85rem', lineHeight: 1.4 }}
                >
                  {errorMessage}
                </div>
              )}
            </div>
          ) : null}
        </div>

        {/* Hidden file input */}
        <input
          ref={fileInputRef}
          type="file"
          accept={accept}
          onChange={handleFileChange}
          style={{ display: 'none' }}
        />
      </div>
    </div>
  );
};

// Delete Confirmation Modal Component
const DeleteConfirmModal = ({
  imageIndex,
  onConfirm,
  onCancel
}) => {
  const { t } = useLanguage();
  const imageNumber = imageIndex !== null ? imageIndex + 1 : null;
  const handleOverlayClick = useCallback((e) => {
    if (e.target === e.currentTarget) {
      onCancel();
    }
  }, [onCancel]);

  return (
    <div className="delete-confirm-modal-overlay" onClick={handleOverlayClick}>
      <div className="delete-confirm-modal">
        <div className="delete-confirm-header">
          <div className="delete-confirm-icon">
            <TrashIcon size={28} />
          </div>
          <h3 className="delete-confirm-title">{t('postReview.postEdit.delete.title')}</h3>
        </div>
        
        <div className="delete-confirm-content">
          <p className="delete-confirm-message">
            {imageNumber !== null
              ? t('postReview.postEdit.delete.messageWithIndex', { index: imageNumber })
              : t('postReview.postEdit.delete.message')}
            <br />
            {t('postReview.postEdit.delete.warning')}
          </p>
        </div>
        
        <div className="delete-confirm-actions">
          <button
            className="delete-confirm-btn cancel"
            onClick={onCancel}
          >
            {t('postReview.postEdit.delete.cancel')}
          </button>
          <button
            className="delete-confirm-btn confirm"
            onClick={onConfirm}
          >
            <TrashIcon size={16} />
            {t('postReview.postEdit.delete.confirm')}
          </button>
        </div>
      </div>
    </div>
  );
};

export default PostEditPage;
